/**
 * Every pixel in this game is drawn here, at boot, into RenderTextures.
 *
 * There are no art assets. A unit is a set of silhouette parameters in
 * `src/data/units.ts`; this module turns those parameters into a top-down
 * soldier with big shoulders, a big weapon and a rim light that tells you at a
 * glance whose side it is on.
 *
 * Art direction, in three rules:
 *   1. The ground is ash and bone. Only units, banners and FX carry chroma.
 *   2. Silhouette first. If it does not read as a black shape at 40 pixels,
 *      it is wrong, and no amount of detail will save it.
 *   3. Warm rim = yours. Cold rim = theirs. Always, everywhere, no exceptions.
 *
 * And one rule learned later, which is why this file is the size it is:
 *   4. A build is an *outline*, not a tint. Ten unit types that share one
 *      ellipse are one unit type with ten colours. Every `VisBuild` here gets
 *      its own path — different height, stance, shoulder mass and head shape —
 *      and every `VisWeapon` gets enough reach to be half of what you read.
 */

import type { Texture} from 'pixi.js';
import { Container, Graphics, Matrix, RenderTexture, type Renderer } from 'pixi.js';
import { C, mix, shade } from '../data/palette.ts';
import { UNIT_TYPES, type UnitType, type UnitVisual, type VisWeapon } from '../data/units.ts';
import { BUILDING_TYPES, type BuildingMotif, type BuildingType } from '../data/buildings.ts';
import { faction, type ArchitectureStyle } from '../data/factions.ts';
import {
  ASHEN_BASELINE,
  vernacularForStyle,
  vernacularOf,
  type Vernacular,
} from './vernacular.ts';
import { Terrain, UF } from '../sim/types.ts';
import { hash2 } from '../core/math.ts';
import {
  MARK_MAX_LUMA,
  TERRAIN_IDS,
  capLuma,
  surfaceTable,
  themeFor,
  type BiomeTheme,
  type Surface,
} from './biome.ts';

/**
 * A lit mark's colour, held under the marks' own ceiling.
 *
 * See `MARK_MAX_LUMA`: the ground itself is capped hard because it is an area,
 * and a mark is allowed a stop more because it is a speck. Every lit facet on
 * a terrain tile goes through here rather than being trusted to the number
 * somebody typed, which is the same argument `FABRIC_MAX` settles on the chart.
 */
function mark(c: number): number {
  return capLuma(c, MARK_MAX_LUMA);
}

/** A shadow mark's colour: dark by construction, so it needs no ceiling. */
function ink(c: number): number {
  return shade(c, 0.7);
}

/**
 * Pixels per tile that unit art is authored at. Sprites are scaled from here,
 * so this is a *resolution* knob and not a size knob: `scene` divides by it to
 * get the draw scale, and raising it buys drawing room without changing how big
 * anything appears. It was raised from 56 when brutes and champions started
 * growing capes and horns that the old box sawed off.
 */
export const ART_UNIT_PX = 72;
export const ART_TILE_PX = 32;

export interface UnitArt {
  /** Frame 0. Kept as its own field so nothing that only wants a still breaks. */
  body: Texture;
  /**
   * The walk cycle: [planted, stepping]. Two frames is all this scale can
   * carry and all it needs — a leg bob is the difference between a company
   * marching and a sheet of decals sliding over the ground.
   *
   * Engines that roll rather than walk get the same texture twice, so nothing
   * has to special-case them and no texture is baked for nothing.
   */
  frames: Texture[];
  weapon: Texture;
  /** Pivot offset for the weapon, as a fraction of 48 — see `bakeUnit`. */
  weaponPivotX: number;
  weaponPivotY: number;
  /** Extra scale to apply when drawing. */
  scale: number;
}

/**
 * Pick a walk frame. `step` is any integer that advances as the unit covers
 * ground — distance travelled in half-tiles is the cheapest honest source, and
 * it means a routing man's legs go faster than a marching one's for free.
 */
export function walkFrame(art: UnitArt, step: number): Texture {
  return art.frames[((step % 2) + 2) % 2];
}

export class TextureBank {
  readonly renderer: Renderer;

  /** [typeIndex][team] → art. Team 0 = warm rim, 1 = cold rim. */
  readonly units: UnitArt[][] = [];
  /**
   * Buildings for the architecture currently in play, indexed by type.
   *
   * Mutated in place by `setArchitecture` rather than replaced, because the
   * scene reads straight through `bank.buildings[typeId]` and a fresh array
   * would leave it holding the old one.
   */
  readonly buildings: Texture[] = [];
  readonly terrain: Texture[][] = [];
  readonly walls: Record<string, Texture> = {};

  /**
   * War-map tokens, used once the camera pulls back past the point where a
   * silhouette is legible. Index by `tokenIndexFor`.
   */
  readonly tokens: Texture[] = [];

  blob!: Texture;
  spark!: Texture;
  smoke!: Texture;
  ember!: Texture;
  blood!: Texture;
  ring!: Texture;
  arrow!: Texture;
  bolt!: Texture;
  stone!: Texture;
  magic!: Texture;
  siegeShot!: Texture;
  crater!: Texture;
  corpse!: Texture;
  scorch!: Texture;
  /** A company standard: white, tinted to the faction accent at draw time. */
  banner!: Texture;
  reticle!: Texture;
  glow!: Texture;
  white!: Texture;

  /** Baked building sets, one per vernacular, cut only when first asked for. */
  private readonly buildingsByStyle = new Map<string, Texture[]>();
  private vernacular: Vernacular = ASHEN_BASELINE;

  /**
   * The country the ground is painted for. See `render/biome.ts` and `setBiome`.
   *
   * Held beside `vernacular` and moved the same way: the two are independent —
   * a Karrun hold can stand in a marsh — so they are two fields and two bakes.
   */
  private theme: BiomeTheme = themeFor(undefined);

  constructor(renderer: Renderer) {
    this.renderer = renderer;
  }

  /** Render a display object into a texture. Public so the scene can bake terrain chunks. */
  bake(g: Container, w: number, h: number, resolution = 1): Texture {
    const rt = RenderTexture.create({ width: w, height: h, resolution, antialias: true });
    this.renderer.render({ container: g, target: rt, clear: true });
    return rt;
  }

  /** Bake with the drawing origin at the texture centre. */
  private bakeCentred(draw: (g: Graphics) => void, size: number): Texture {
    const g = new Graphics();
    draw(g);
    const holder = new Container();
    holder.addChild(g);
    holder.setFromMatrix(new Matrix(1, 0, 0, 1, size / 2, size / 2));
    return this.bake(holder, size, size);
  }

  build(): void {
    this.buildPrimitives();
    this.buildTokens();
    this.buildTerrain();
    this.buildWalls();
    this.buildUnits();
    this.setVernacular('ashen');
  }

  /**
   * Point the building bank at a people's vernacular.
   *
   * Vernaculars are baked on demand and kept: a run only ever sees one
   * settlement, so baking all twelve at boot would spend the whole texture
   * budget on eleven towns nobody is going to look at. The first call for a
   * people costs roughly a twelfth of the old building bake; every call after it
   * is free, which is what lets the contact sheet put four races side by side.
   *
   * **Nothing called this before.** `build()` set `'stone'` and no other line in
   * the codebase ever moved it, so every settlement in the game — Karrun-Dûr,
   * the Moonbound, the Pale Litany — was drawn in Ashen grey rubble, and the
   * whole architecture table was dead code the day it was written. Wiring it up
   * was worth more than anything added to it since. `Scene.render` now watches
   * `world.raceId` and calls this when it moves.
   */
  setVernacular(id: string): void {
    this.applySet(id, () => vernacularOf(id));
  }

  /** The same, for anything that only knows a faction. See `vernacularForStyle`. */
  setArchitecture(style: ArchitectureStyle): void {
    this.applySet(`style:${style}`, () => vernacularForStyle(style));
  }

  private applySet(key: string, resolve: () => Vernacular): void {
    const vern = resolve();
    let set = this.buildingsByStyle.get(key);
    if (!set) {
      set = BUILDING_TYPES.map((def) => this.bakeBuilding(def, vern));
      this.buildingsByStyle.set(key, set);
    }
    this.vernacular = vern;
    this.buildings.length = 0;
    for (const t of set) this.buildings.push(t);
  }

  /** Which people the building bank is currently showing. */
  get vernacularId(): string {
    return this.vernacular.id;
  }

  /**
   * Token shapes, drawn in white and tinted at draw time. Shape carries role,
   * tint carries side — so a pulled-back view reads like a war table: you can
   * see that the wedge of diamonds is cavalry and that it is coming for your
   * left, without being able to see a single soldier.
   */
  private buildTokens(): void {
    const S = 40;
    const R = 13;
    const shapes: Array<(g: Graphics) => void> = [
      // 0 infantry — a solid square, the block of the line
      (g) => g.rect(-R * 0.8, -R * 0.8, R * 1.6, R * 1.6).fill(0xffffff),
      // 1 archer — an inverted triangle, arrow-like
      (g) => g.poly([-R, -R * 0.8, R, -R * 0.8, 0, R]).fill(0xffffff),
      // 2 cavalry — a diamond pointing the way it faces
      (g) => g.poly([R * 1.15, 0, 0, -R * 0.85, -R * 0.8, 0, 0, R * 0.85]).fill(0xffffff),
      // 3 siege — a heavy hexagon
      (g) => {
        const pts: number[] = [];
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          pts.push(Math.cos(a) * R, Math.sin(a) * R);
        }
        g.poly(pts).fill(0xffffff);
      },
      // 4 caster — a circle with a hollow centre
      (g) => {
        g.circle(0, 0, R * 0.95).fill(0xffffff);
        g.circle(0, 0, R * 0.4).fill({ color: 0x000000, alpha: 1 });
      },
      // 5 large/monstrous — a fat rounded blob
      (g) => g.circle(0, 0, R * 1.15).fill(0xffffff),
      // 6 villager — a small dot
      (g) => g.circle(0, 0, R * 0.45).fill(0xffffff),
      // 7 champion — a star
      (g) => {
        const pts: number[] = [];
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
          const rr = i % 2 === 0 ? R * 1.25 : R * 0.52;
          pts.push(Math.cos(a) * rr, Math.sin(a) * rr);
        }
        g.poly(pts).fill(0xffffff);
      },
      // 8 swarm — a cluster, because the point of a swarm is that it is many
      (g) => {
        g.circle(-R * 0.42, -R * 0.36, R * 0.42).fill(0xffffff);
        g.circle(R * 0.46, -R * 0.2, R * 0.38).fill(0xffffff);
        g.circle(-R * 0.06, R * 0.5, R * 0.44).fill(0xffffff);
      },
      // 9 flier — a pair of swept wings, which reads the same at any rotation
      (g) => {
        g.poly([0, -R * 0.3, -R * 1.3, -R * 0.95, -R * 0.5, R * 0.25, 0, R * 0.5]).fill(0xffffff);
        g.poly([0, -R * 0.3, R * 1.3, -R * 0.95, R * 0.5, R * 0.25, 0, R * 0.5]).fill(0xffffff);
      },
    ];
    for (const draw of shapes) {
      this.tokens.push(this.bakeCentred(draw, S));
    }
  }

  // ── Primitives ───────────────────────────────────────────────────────────

  private buildPrimitives(): void {
    this.white = this.bakeCentred((g) => {
      g.rect(-4, -4, 8, 8).fill(0xffffff);
    }, 8);

    // Soft radial blob — shadows, glows, smoke cores.
    this.blob = this.bakeCentred((g) => {
      for (let i = 12; i >= 1; i--) {
        g.circle(0, 0, (i / 12) * 30).fill({ color: 0xffffff, alpha: 0.055 });
      }
    }, 64);

    this.glow = this.bakeCentred((g) => {
      for (let i = 16; i >= 1; i--) {
        const t = i / 16;
        g.circle(0, 0, t * 46).fill({ color: 0xffffff, alpha: 0.035 * (1 - t) + 0.01 });
      }
    }, 96);

    this.spark = this.bakeCentred((g) => {
      g.moveTo(-9, 0).lineTo(9, 0).stroke({ width: 2.4, color: 0xffffff, alpha: 1, cap: 'round' });
      g.circle(0, 0, 2.1).fill({ color: 0xffffff });
    }, 24);

    this.smoke = this.bakeCentred((g) => {
      for (let i = 10; i >= 1; i--) {
        g.circle(0, 0, (i / 10) * 26).fill({ color: 0xffffff, alpha: 0.05 });
      }
      g.circle(-6, -4, 9).fill({ color: 0xffffff, alpha: 0.06 });
      g.circle(7, 3, 7).fill({ color: 0xffffff, alpha: 0.05 });
    }, 56);

    this.ember = this.bakeCentred((g) => {
      g.circle(0, 0, 3.2).fill({ color: 0xffffff });
      g.circle(0, 0, 6.5).fill({ color: 0xffffff, alpha: 0.28 });
    }, 20);

    this.blood = this.bakeCentred((g) => {
      g.ellipse(0, 0, 6, 4).fill({ color: 0xffffff });
      g.circle(6, 2, 2.2).fill({ color: 0xffffff, alpha: 0.9 });
      g.circle(-5, -3, 1.7).fill({ color: 0xffffff, alpha: 0.8 });
    }, 24);

    this.ring = this.bakeCentred((g) => {
      g.circle(0, 0, 28).stroke({ width: 3, color: 0xffffff, alpha: 1 });
      g.circle(0, 0, 22).stroke({ width: 1, color: 0xffffff, alpha: 0.45 });
    }, 64);

    this.reticle = this.bakeCentred((g) => {
      g.circle(0, 0, 60).stroke({ width: 2, color: 0xffffff, alpha: 0.9 });
      g.circle(0, 0, 52).stroke({ width: 1, color: 0xffffff, alpha: 0.3 });
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        g.moveTo(Math.cos(a) * 44, Math.sin(a) * 44)
          .lineTo(Math.cos(a) * 58, Math.sin(a) * 58)
          .stroke({ width: 2, color: 0xffffff, alpha: 0.75 });
      }
    }, 128);

    // Projectiles.
    this.arrow = this.bakeCentred((g) => {
      g.moveTo(-11, 0).lineTo(9, 0).stroke({ width: 1.6, color: C.bone, cap: 'round' });
      g.poly([9, 0, 4, -2.4, 4, 2.4]).fill(C.boneDim);
      g.poly([-11, 0, -7, -3, -7, 3]).fill({ color: C.boneDeep, alpha: 0.9 });
    }, 28);

    this.bolt = this.bakeCentred((g) => {
      g.moveTo(-8, 0).lineTo(8, 0).stroke({ width: 2.6, color: 0x9c968a, cap: 'round' });
      g.poly([9, 0, 3, -3, 3, 3]).fill(0xcfc7b6);
    }, 24);

    this.stone = this.bakeCentred((g) => {
      g.poly([-5, -3, 2, -6, 6, 0, 3, 5, -4, 4]).fill(0x6a655c);
      g.poly([-5, -3, 2, -6, 0, -1]).fill(0x847e73);
    }, 20);

    this.magic = this.bakeCentred((g) => {
      g.circle(0, 0, 5).fill({ color: 0xffffff });
      g.circle(0, 0, 10).fill({ color: 0xffffff, alpha: 0.35 });
      g.circle(0, 0, 15).fill({ color: 0xffffff, alpha: 0.12 });
    }, 40);

    this.siegeShot = this.bakeCentred((g) => {
      g.circle(0, 0, 8).fill(0x4a453e);
      g.circle(-2.5, -2.5, 3.4).fill({ color: 0x6d675e, alpha: 0.8 });
    }, 28);

    // Ground decals.
    this.crater = this.bakeCentred((g) => {
      g.ellipse(0, 0, 34, 26).fill({ color: 0x100e0c, alpha: 0.5 });
      g.ellipse(0, 0, 26, 19).fill({ color: 0x1c1815, alpha: 0.45 });
      g.ellipse(-4, -3, 14, 10).fill({ color: 0x2a2420, alpha: 0.35 });
    }, 80);

    this.scorch = this.bakeCentred((g) => {
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2;
        const r = 16 + (i % 3) * 6;
        g.circle(Math.cos(a) * r * 0.55, Math.sin(a) * r * 0.5, r * 0.5).fill({
          color: 0x0d0b09,
          alpha: 0.24,
        });
      }
    }, 80);

    this.corpse = this.bakeCentred((g) => {
      g.ellipse(0, 0, 11, 6).fill({ color: 0x1a1613, alpha: 0.85 });
      g.ellipse(-6, -1, 4, 3.4).fill({ color: 0x241f1a, alpha: 0.9 });
      g.ellipse(6, 1, 3, 2.6).fill({ color: 0x241f1a, alpha: 0.75 });
    }, 40);

    /**
     * The company standard.
     *
     * Tall enough to clear a rank of men and outlined like a unit, because that
     * is what it is for: at tactical zoom the pole is how you find the leader,
     * and a pennant that does not break the skyline finds nobody. Drawn white
     * and tinted to the faction accent by whoever plants it.
     */
    this.banner = this.bakeCentred((g) => {
      g.moveTo(0, 26).lineTo(0, -30).stroke({ width: 5.4, color: 0x0d0b09, alpha: 0.9, cap: 'round' });
      g.moveTo(0, 26).lineTo(0, -30).stroke({ width: 2.6, color: 0xffffff, cap: 'round' });
      const flag = [0, -30, 22, -25, 17, -17, 22, -9, 0, -13];
      g.poly(flag).fill({ color: 0x0d0b09, alpha: 0.9 }).stroke({ width: 4, color: 0x0d0b09, alpha: 0.9, join: 'round' });
      g.poly(flag).fill(0xffffff);
      g.circle(0, -31, 3.2).fill({ color: 0x0d0b09, alpha: 0.9 });
      g.circle(0, -31, 2.0).fill(0xffffff);
    }, 72);
  }

  // ── Terrain ──────────────────────────────────────────────────────────────

  /**
   * The terrain layer's *marks* — and only its marks.
   *
   * These used to be the whole of the ground: an opaque 32-pixel tile per
   * terrain, four variants deep, butted edge to edge. That is why the
   * settlement's floor read as a tiled floor, and why grass met forest on a
   * straight line down a tile boundary.
   *
   * The colour, the shape and the coarse grain of the ground now come from
   * `render/ground.ts`, baked per chunk at six pixels to a tile and stretched
   * under this layer. **What is left here is transparent**: tufts, pebbles,
   * reeds, cracks, planks and ripples at the pitch a player sees when they wind
   * the camera all the way in, over ground that already has a colour and a
   * light. Two layers, two pitches — the split `ui/relief.ts` argued for, and
   * the reason neither of them has to be a compromise.
   *
   * Re-baked when the country changes, which is once per world.
   */
  private buildTerrain(): void {
    const surf = surfaceTable(this.theme);
    for (const t of TERRAIN_IDS) {
      const old = this.terrain[t];
      const variants: Texture[] = [];
      for (let v = 0; v < 4; v++) {
        variants.push(this.bakeTerrainTile(t, surf[t], v));
      }
      this.terrain[t] = variants;
      if (old) for (const tex of old) tex.destroy(true);
    }
  }

  /**
   * Point the ground at a country. See `render/biome.ts`.
   *
   * A string compare a frame in `Scene.render`, and the bake behind it happens
   * once per world — the same shape as `setVernacular`, and for the same
   * reason: there are three doors into a settlement now and a renderer that
   * reads the world it is given cannot be out of step with it.
   */
  setBiome(biome: string | undefined): void {
    const next = themeFor(biome);
    if (next.id === this.theme.id) return;
    this.theme = next;
    this.buildTerrain();
  }

  /** Which country the ground is currently painted for. */
  get biomeId(): string {
    return this.theme.id;
  }

  private bakeTerrainTile(terrain: number, pal: Surface, variant: number): Texture {
    const S = ART_TILE_PX;
    const g = new Graphics();

    /**
     * Grain: a scatter of flecks, seeded per variant — and **no base fill**.
     *
     * The one line that used to be here, `g.rect(0, 0, S, S).fill(pal.base)`,
     * is the whole of what made this a tiled floor. Everything below now
     * composites over the chunk's own ground.
     */
    for (let i = 0; i < 26; i++) {
      const x = hash2(i, variant, 17) * S;
      const y = hash2(i, variant, 91) * S;
      const r = 0.7 + hash2(i, variant, 55) * 2.1;
      const light = hash2(i, variant, 7) > 0.5;
      g.circle(x, y, r).fill({ color: light ? pal.alt : pal.ink, alpha: 0.33 });
    }

    switch (terrain) {
      case Terrain.Forest: {
        /**
         * The floor under the wood — **not** trees.
         *
         * This used to draw three dead trunks per tile, which was right when
         * nothing else did: forest was a terrain and there was no such thing as
         * a tree. There is now. `sim/systems/nodes.ts` stands real trees on this
         * ground, `render/nodeart.ts` gives each one a silhouette, and a
         * dead trunk baked into every tile underneath them was a second, worse
         * forest drawn through the first.
         *
         * What a forest floor has that open ground does not is root, deadfall
         * and low brush, so that is what is here: faceted scrub lit from the
         * north-west like everything else, and roots running out from under it.
         */
        for (let i = 0; i < 3; i++) {
          const x = 6 + hash2(i, variant, 3) * (S - 12);
          const y = 8 + hash2(i, variant, 29) * (S - 14);
          const r = 2.6 + hash2(i, variant, 61) * 2.6;
          // A bush as three facets: shaded mass, lit north-west plane, contact
          // shadow to the south-east. The same three planes as a boulder, a
          // roof and a tower — one light, one language.
          g.ellipse(x + 1, y + r * 0.55, r * 1.05, r * 0.42).fill({ color: ink(pal.ink), alpha: 0.4 });
          g.poly([x - r, y + r * 0.5, x - r * 0.5, y - r, x + r * 0.6, y - r * 0.4, x + r, y + r * 0.55])
            .fill({ color: mark(shade(pal.base, 1.5)), alpha: 0.85 });
          g.poly([x - r, y + r * 0.5, x - r * 0.5, y - r, x - r * 0.05, y + r * 0.1]).fill({
            color: mark(shade(pal.alt, 1.25)),
            alpha: 0.8,
          });
          for (let j = 0; j < 2; j++) {
            const a = hash2(i * 3 + j, variant, 63) * Math.PI * 2;
            g.moveTo(x, y + r * 0.5)
              .lineTo(x + Math.cos(a) * r * 2.1, y + r * 0.5 + Math.sin(a) * r * 0.9)
              .stroke({ width: 1.1, color: ink(pal.ink), alpha: 0.55 });
          }
        }
        break;
      }
      case Terrain.StoneOutcrop: {
        // Broken plates: a shaded mass, a lit north-west facet, a cast shadow.
        // The colours come off the country's own rock rather than one grey, so
        // a snowline shelf and a desert shelf are the same stone in two lights.
        for (let i = 0; i < 4; i++) {
          const x = 5 + hash2(i, variant, 13) * (S - 10);
          const y = 5 + hash2(i, variant, 77) * (S - 10);
          const r = 3 + hash2(i, variant, 41) * 4.5;
          g.poly([x - r * 0.7, y + r * 0.9, x + r * 1.1, y + r * 0.8, x + r * 1.3, y + r * 1.2, x - r * 0.5, y + r * 1.2])
            .fill({ color: ink(pal.ink), alpha: 0.45 });
          g.poly([x - r, y + r * 0.6, x - r * 0.4, y - r, x + r * 0.7, y - r * 0.5, x + r, y + r * 0.7]).fill(
            mark(shade(pal.alt, 1.55)),
          );
          g.poly([x - r, y + r * 0.6, x - r * 0.4, y - r, x - r * 0.1, y + r * 0.2]).fill(
            mark(shade(pal.alt, 1.95)),
          );
        }
        break;
      }
      case Terrain.IronSeam: {
        /**
         * Ore in the rock: a rust streak with a lit lip above it, which is what
         * makes a seam read as something split open rather than as a scratch.
         *
         * Three, not five, and at half the ink. A seam field is four and a half
         * thousand tiles on a mountain map, and five saturated rust strokes a
         * tile came out as orange chevrons scattered over the whole province —
         * visible in `cityart-02-water-z22` and against the one rule in
         * `palette.ts` that outranks everything here. Ore is *in* the rock.
         */
        for (let i = 0; i < 3; i++) {
          const x = 4 + hash2(i, variant, 23) * (S - 8);
          const y = 4 + hash2(i, variant, 87) * (S - 8);
          const run = 5 - hash2(i, variant, 5) * 10;
          g.moveTo(x, y - 1)
            .lineTo(x + run, y + 3)
            .stroke({ width: 1.1, color: mark(shade(pal.alt, 1.7)), alpha: 0.4 });
          g.moveTo(x, y)
            .lineTo(x + run, y + 4)
            .stroke({ width: 1.6, color: mark(0x7a583a), alpha: 0.5 });
        }
        break;
      }
      case Terrain.Marsh: {
        // Standing water in the hollows: a dark pool with a cold sky-glint on
        // its north-west lip, and reeds standing out of it.
        for (let i = 0; i < 3; i++) {
          const x = 6 + hash2(i, variant, 33) * (S - 12);
          const y = 6 + hash2(i, variant, 67) * (S - 12);
          g.ellipse(x, y, 5.5, 3).fill({ color: ink(shade(pal.ink, 0.8)), alpha: 0.7 });
          g.ellipse(x - 1, y - 0.8, 3, 1.4).fill({ color: mark(shade(pal.alt, 1.5)), alpha: 0.4 });
          for (let j = 0; j < 3; j++) {
            const rx = x - 4 + hash2(i * 4 + j, variant, 69) * 8;
            g.moveTo(rx, y + 2)
              .lineTo(rx + 0.8, y - 4 - hash2(i * 4 + j, variant, 70) * 3)
              .stroke({ width: 0.9, color: mark(shade(pal.alt, 1.2)), alpha: 0.55 });
          }
        }
        break;
      }
      case Terrain.River: {
        /**
         * A ford, and the read wanted is *you can see the bottom*.
         *
         * The opaque wash that used to be here has gone: the channel's colour
         * and the shape of its bed now come from the chunk's own ground, where
         * they can shade into the bank instead of stopping at a tile edge. What
         * is left is the surface — the standing waves a shallow makes over
         * stones, and the stones showing through them.
         */
        for (let i = 0; i < 3; i++) {
          const x = hash2(i, variant, 141) * S;
          const y = hash2(i, variant, 143) * S;
          g.ellipse(x, y, 2.2, 1.2).fill({ color: ink(pal.ink), alpha: 0.5 });
        }
        /**
         * **Wavelets, not lines**, and this is a fault the first cut shipped.
         *
         * A stroke that runs edge to edge in a 32-pixel tile is a stroke that
         * runs edge to edge across the *map*: there are four variants and a
         * river is a hundred tiles long, so four horizontal lines per tile
         * joined up into a barcode of continuous blue rules from bank to bank.
         * A standing wave is a short thing over a stone. Placed by hash and
         * kept clear of the edges, so nothing can join.
         */
        for (let i = 0; i < 5; i++) {
          const x = 3 + hash2(i, variant, 145) * (S - 12);
          const y = 3 + hash2(i, variant, 147) * (S - 6);
          const w = 4 + hash2(i, variant, 149) * 5;
          g.moveTo(x, y)
            .quadraticCurveTo(x + w * 0.5, y - 2.2, x + w, y)
            .stroke({ width: 1.1, color: mark(0x6f8e9c), alpha: 0.34 });
        }
        break;
      }
      /**
       * Deep water: the same current, drawn slower and colder.
       *
       * Fewer lines, wider apart, lower contrast — a surface with nothing under
       * it you can see. The read wanted at a glance is *depth*, and the cheapest
       * honest cue for that is the bed disappearing: no stones, no standing
       * waves, and a single long swell that catches the sky.
       */
      case Terrain.Deep: {
        // Two long swells rather than the ford's five short ones, and kept off
        // the tile edges for the reason written up there.
        for (let i = 0; i < 2; i++) {
          const x = 2 + hash2(i, variant, 151) * (S - 18);
          const y = 4 + hash2(i, variant, 153) * (S - 8);
          const w = 11 + hash2(i, variant, 157) * 5;
          g.moveTo(x, y)
            .quadraticCurveTo(x + w * 0.5, y - 1.6, x + w, y)
            .stroke({ width: 1.4, color: mark(0x4d6e7c), alpha: 0.18 });
        }
        break;
      }
      /**
       * A bridge, drawn as a structure and not as a floor.
       *
       * Two things were wrong with the planks that shipped. They always ran the
       * same way, so a bridge crossing an east–west road was a deck laid along
       * it rather than across it; and they had no thickness, so the one built
       * object on the water was the only thing in the game with no shadow under
       * it. Both are fixed here: `variant & 2` carries the run of the deck —
       * `scene.ts` reads the neighbours and picks — and the deck is drawn the
       * way `curtainart.ts` draws a wall, as a lit top over a shaded edge with
       * the water darkened beneath it.
       */
      case Terrain.Bridge: {
        const alongY = (variant & 2) !== 0;
        const deck = mark(pal.base);
        const lit = mark(shade(pal.alt, 1.5));
        const edge = ink(shade(pal.ink, 0.75));
        // The water in the shadow of the span.
        g.rect(0, 0, S, S).fill({ color: 0x000000, alpha: 0.22 });
        for (let i = 0; i < 5; i++) {
          const t = (i / 5) * S + 1;
          const wide = S / 5 - 1.5;
          if (alongY) {
            g.rect(0, t, S, wide).fill({ color: deck, alpha: 0.96 });
            g.rect(0, t, S, 1.3).fill({ color: lit, alpha: 0.85 });
            g.rect(0, t + wide - 1, S, 1).fill({ color: edge, alpha: 0.6 });
          } else {
            g.rect(t, 0, wide, S).fill({ color: deck, alpha: 0.96 });
            g.rect(t, 0, 1.3, S).fill({ color: lit, alpha: 0.85 });
            g.rect(t + wide - 1, 0, 1, S).fill({ color: edge, alpha: 0.6 });
          }
        }
        // Kerbs down the exposed sides, with the north-west one catching the
        // light — this is the whole of what makes it a bridge and not a raft.
        if (alongY) {
          g.rect(0, 0, S, 2.4).fill({ color: lit, alpha: 0.9 });
          g.rect(0, S - 2.6, S, 2.6).fill({ color: edge, alpha: 0.9 });
        } else {
          g.rect(0, 0, 2.4, S).fill({ color: lit, alpha: 0.9 });
          g.rect(S - 2.6, 0, 2.6, S).fill({ color: edge, alpha: 0.9 });
        }
        break;
      }
      case Terrain.Ruin: {
        // Old footings: a block, its lit top, and the shadow it throws to the
        // south-east. Three planes, the same three as everything else.
        for (let i = 0; i < 2; i++) {
          const x = 6 + hash2(i, variant, 19) * (S - 14);
          const y = 6 + hash2(i, variant, 71) * (S - 14);
          const w = 5 + hash2(i, variant, 3) * 7;
          const h = 4 + hash2(i, variant, 9) * 5;
          g.rect(x + 1.5, y + 1.5, w, h).fill({ color: ink(pal.ink), alpha: 0.45 });
          g.rect(x, y, w, h).fill(mark(shade(pal.alt, 1.35)));
          g.rect(x, y, w, 1.6).fill(mark(shade(pal.alt, 1.75)));
          g.rect(x, y + h - 1.4, w, 1.4).fill({ color: ink(pal.ink), alpha: 0.6 });
        }
        break;
      }
      case Terrain.Road: {
        /**
         * A worked track: two ruts running the length of it, and the stones
         * thrown out of them. The ruts are what makes it read as travelled.
         *
         * **Along the road, which is not always east–west.** These ran flat in
         * every tile, so a north–south track came out rung like a ladder — the
         * same fault the bridge had, and it takes the same fix: `variant & 2`
         * carries the run of the road, read off the neighbours in
         * `Scene.tileVariant`. A rut *should* meet the rut in the next tile, so
         * unlike the drift and the dune these stay full width; what was wrong
         * was the axis, not the length.
         */
        const alongY = (variant & 2) !== 0;
        for (let i = 0; i < 2; i++) {
          const off = S * (0.34 + i * 0.3) + (hash2(i, variant, 111) - 0.5) * 3;
          if (alongY) {
            g.moveTo(off, 0)
              .bezierCurveTo(off - 2, S * 0.35, off + 2, S * 0.7, off, S)
              .stroke({ width: 2.4, color: ink(pal.ink), alpha: 0.32 });
          } else {
            g.moveTo(0, off)
              .bezierCurveTo(S * 0.35, off - 2, S * 0.7, off + 2, S, off)
              .stroke({ width: 2.4, color: ink(pal.ink), alpha: 0.32 });
          }
        }
        for (let i = 0; i < 10; i++) {
          const x = hash2(i, variant, 101) * S;
          const y = hash2(i, variant, 103) * S;
          g.circle(x, y, 1 + hash2(i, variant, 107) * 1.6).fill({
            color: mark(shade(pal.alt, 1.35)),
            alpha: 0.5,
          });
        }
        break;
      }
      case Terrain.Ditch: {
        // A hole, read as a hole: a lit lip along the top edge, deep shadow
        // pooling toward the bottom, and spoil heaped on the near side. The
        // ground beneath already digs the cut — this is the lip and the stakes.
        g.rect(0, 0, S, S * 0.2).fill({ color: mark(shade(pal.alt, 1.4)), alpha: 0.55 });
        g.rect(0, S * 0.22, S, S * 0.46).fill({ color: 0x0d0c0a, alpha: 0.5 });
        for (let i = 0; i < 5; i++) {
          const x = hash2(i, variant, 211) * S;
          const y = S * 0.74 + hash2(i, variant, 223) * S * 0.2;
          g.circle(x, y, 1.4 + hash2(i, variant, 227) * 2).fill({
            color: mark(shade(pal.alt, 1.1)),
            alpha: 0.6,
          });
        }
        // Stakes at the bottom, for the ones who climb down anyway.
        for (let i = 0; i < 3; i++) {
          const x = 5 + hash2(i, variant, 233) * (S - 10);
          g.moveTo(x, S * 0.66)
            .lineTo(x + 1.5, S * 0.34)
            .stroke({ width: 1.4, color: mark(0x4a4136), alpha: 0.75 });
        }
        break;
      }
      case Terrain.Leyline: {
        // A seam of cold light under the ash, not on it: a thin vein wandering
        // across the tile, and a handful of motes above it. Drawn faint on
        // purpose — the ground stays under the units, and a player who is
        // looking for a leyline will find a patch of them, never one tile.
        const y0 = S * (0.25 + hash2(0, variant, 311) * 0.5);
        g.moveTo(0, y0)
          .bezierCurveTo(S * 0.35, y0 - S * 0.2, S * 0.65, y0 + S * 0.22, S, y0)
          .stroke({ width: 1.3, color: 0x6f8ec4, alpha: 0.45 });
        g.moveTo(0, y0)
          .bezierCurveTo(S * 0.35, y0 - S * 0.2, S * 0.65, y0 + S * 0.22, S, y0)
          .stroke({ width: 3.4, color: 0x3d5c8a, alpha: 0.16 });
        for (let i = 0; i < 4; i++) {
          const x = hash2(i, variant, 317) * S;
          const y = hash2(i, variant, 331) * S;
          g.circle(x, y, 0.7 + hash2(i, variant, 337) * 1.1).fill({ color: 0x9fb6e0, alpha: 0.35 });
        }
        break;
      }
      /**
       * Open ground, and this is where a country is legible at close zoom.
       *
       * The chunk's ground layer carries the colour, the shape and the coarse
       * grain of every biome, but it is baked at four pixels to a tile — wind
       * the camera in to seventy-four pixels a tile and it is a soft wash. The
       * crisp mark that says *snow*, *sand*, *scree* or *fen* has to be drawn
       * at this pitch, and open ground is the only surface a country wholly
       * owns: a forest floor is a forest floor in any weather, but bare ground
       * is precisely what a country *is*.
       *
       * One mark each, all of them lit from the north-west, none of them a
       * symbol. This is the settlement's half of "every biome on one
       * vocabulary".
       */
      default: {
        switch (this.theme.open) {
          case 'drift': {
            /**
             * Snow: sastrugi — short wind-packed crests with a lit north-west
             * face, and the scoured grey of what is under them in the lee.
             *
             * **Short, and this is the fault the first cut shipped.** These
             * crests ran from `-2` to `S + 2`, which is edge to edge of a
             * thirty-two pixel tile — and there are four variants against a
             * snowfield forty thousand tiles wide, so they joined end to end
             * into unbroken horizontal rules from one side of the map to the
             * other. `cityart-01-hold-ice-z30` is a barcode. It is exactly the
             * fault the river's wavelets were fixed for two cases above, in a
             * country where it covers everything rather than a channel.
             *
             * The long drift *shape* is not lost by making these short: it lives
             * in the ground layer's `drift` fabric, which is sampled in world
             * space and knows nothing about tile boundaries. That is the whole
             * reason the two layers are two.
             */
            for (let i = 0; i < 4; i++) {
              const x = 2 + hash2(i, variant, 401) * (S - 16);
              const y = 3 + hash2(i, variant, 402) * (S - 6);
              const w = 8 + hash2(i, variant, 403) * 6;
              const rise = 1.6 + hash2(i, variant, 404) * 2.2;
              g.moveTo(x, y)
                .quadraticCurveTo(x + w * 0.5, y + rise, x + w, y)
                .stroke({ width: 3, color: mark(0xb9c6d4), alpha: 0.26 });
              g.moveTo(x, y - 1.3)
                .quadraticCurveTo(x + w * 0.5, y + rise - 1.3, x + w, y - 1.3)
                .stroke({ width: 1.2, color: mark(0xdae6f2), alpha: 0.4 });
            }
            for (let i = 0; i < 5; i++) {
              const x = hash2(i, variant, 407) * S;
              const y = hash2(i, variant, 409) * S;
              g.circle(x, y, 0.6 + hash2(i, variant, 411) * 1.1).fill({
                color: mark(0xe4eef8),
                alpha: 0.3,
              });
            }
            break;
          }
          case 'dune': {
            /**
             * Sand: a slip face. The crest catches the light and the lee falls
             * away into shadow, which is the only cue a dune has.
             *
             * Bounded inside the tile for the reason written at `drift` — these
             * ran edge to edge too, and four variants of a full-width curve
             * repeated across a sand sea is corduroy. The long warped crests
             * are the ground layer's `dune` fabric; what belongs at this pitch
             * is the *lip* of one, and the ripple beside it.
             */
            for (let i = 0; i < 3; i++) {
              const x = 1 + hash2(i, variant, 421) * (S - 18);
              const y = 5 + hash2(i, variant, 422) * (S - 10);
              const w = 10 + hash2(i, variant, 423) * 7;
              g.moveTo(x, y + 2.5)
                .quadraticCurveTo(x + w * 0.5, y - 3, x + w, y + 1)
                .stroke({ width: 4, color: ink(pal.ink), alpha: 0.26 });
              g.moveTo(x, y + 0.8)
                .quadraticCurveTo(x + w * 0.5, y - 4.6, x + w, y - 0.6)
                .stroke({ width: 1.5, color: mark(shade(pal.alt, 1.5)), alpha: 0.46 });
            }
            for (let i = 0; i < 5; i++) {
              const x = hash2(i, variant, 425) * (S - 9);
              const y = hash2(i, variant, 427) * S;
              g.moveTo(x, y)
                .lineTo(x + 6 + hash2(i, variant, 429) * 3, y - 1.4)
                .stroke({ width: 0.8, color: mark(shade(pal.alt, 1.25)), alpha: 0.22 });
            }
            break;
          }
          case 'scree': {
            // High stone: chips and shatter, small and many. The country the
            // relief layer exists for still needs something under a boot.
            for (let i = 0; i < 7; i++) {
              const x = hash2(i, variant, 431) * S;
              const y = hash2(i, variant, 433) * S;
              const r = 1.2 + hash2(i, variant, 437) * 2.2;
              g.poly([x - r, y + r * 0.5, x - r * 0.3, y - r, x + r, y + r * 0.4]).fill({
                color: mark(shade(pal.alt, 1.6)),
                alpha: 0.7,
              });
              g.poly([x - r, y + r * 0.5, x - r * 0.3, y - r, x - r * 0.15, y + r * 0.1]).fill({
                color: mark(shade(pal.alt, 2.0)),
                alpha: 0.6,
              });
            }
            break;
          }
          case 'litter': {
            // A wooded country's open ground is still a clearing in a wood:
            // needle, leaf and the odd fallen branch.
            for (let i = 0; i < 9; i++) {
              const x = hash2(i, variant, 441) * S;
              const y = hash2(i, variant, 443) * S;
              const a = hash2(i, variant, 447) * Math.PI;
              const l = 1.6 + hash2(i, variant, 449) * 2.6;
              g.moveTo(x - Math.cos(a) * l, y - Math.sin(a) * l)
                .lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l)
                .stroke({ width: 1.1, color: mark(shade(pal.alt, 1.3)), alpha: 0.45 });
            }
            break;
          }
          case 'reed': {
            // Fen: tussocks standing out of wet ground, and the wet showing
            // between them.
            for (let i = 0; i < 4; i++) {
              const x = 3 + hash2(i, variant, 451) * (S - 6);
              const y = 4 + hash2(i, variant, 453) * (S - 8);
              g.ellipse(x, y + 1.4, 3.4, 1.3).fill({ color: ink(pal.ink), alpha: 0.4 });
              for (let j = 0; j < 4; j++) {
                const dx = (hash2(i * 5 + j, variant, 457) - 0.5) * 5;
                g.moveTo(x + dx * 0.4, y + 1)
                  .lineTo(x + dx, y - 3.4 - hash2(i * 5 + j, variant, 459) * 2.6)
                  .stroke({ width: 0.9, color: mark(shade(pal.alt, 1.3)), alpha: 0.6 });
              }
            }
            break;
          }
          case 'shingle': {
            // A shore: a fine ripple and shell grit, the smoothest ground on
            // any map and drawn as such.
            // Short, not full width: see `drift`. A beach ripple that spans the
            // tile spans the shore.
            for (let i = 0; i < 5; i++) {
              const x = 1 + hash2(i, variant, 460) * (S - 14);
              const y = hash2(i, variant, 461) * S;
              const w = 8 + hash2(i, variant, 462) * 5;
              g.moveTo(x, y)
                .quadraticCurveTo(x + w * 0.5, y + 1.4, x + w, y)
                .stroke({ width: 1, color: mark(shade(pal.alt, 1.35)), alpha: 0.3 });
            }
            for (let i = 0; i < 6; i++) {
              const x = hash2(i, variant, 463) * S;
              const y = hash2(i, variant, 467) * S;
              g.circle(x, y, 0.6 + hash2(i, variant, 469) * 0.9).fill({
                color: mark(shade(pal.alt, 1.7)),
                alpha: 0.4,
              });
            }
            break;
          }
          case 'rubble': {
            // Somebody was here before: a course of stone under the ash, at the
            // angle their street ran and not at the angle of the tile grid.
            for (let i = 0; i < 3; i++) {
              const x = hash2(i, variant, 471) * S;
              const y = hash2(i, variant, 473) * S;
              const w = 3 + hash2(i, variant, 477) * 6;
              g.rect(x + 1, y + 1, w, 2.6).fill({ color: ink(pal.ink), alpha: 0.4 });
              g.rect(x, y, w, 2.6).fill({ color: mark(shade(pal.alt, 1.4)), alpha: 0.65 });
              g.rect(x, y, w, 0.9).fill({ color: mark(shade(pal.alt, 1.8)), alpha: 0.55 });
            }
            break;
          }
          default: {
            /**
             * Turf: tussock, the stones a plough turns up, and the faint
             * lineation of ground somebody has worked. The baseline, and what
             * every hold in this game was.
             *
             * More of it than the first cut had, and with a contact shadow
             * under each clump. Six three-blade tufts at alpha 0.4 over an
             * unlit brown field is nothing at all at the zoom a player plays
             * at — `cityart-01-hold-plain-z30` was the emptiest of the ten
             * countries by a distance, which is the wrong answer for the one
             * every new game starts in.
             */
            for (let i = 0; i < 9; i++) {
              const x = 2 + hash2(i, variant, 481) * (S - 4);
              const y = 3 + hash2(i, variant, 483) * (S - 5);
              g.ellipse(x, y + 1.6, 2.6, 0.9).fill({ color: ink(pal.ink), alpha: 0.34 });
              for (let j = 0; j < 3; j++) {
                const dx = (hash2(i * 4 + j, variant, 487) - 0.5) * 3.6;
                g.moveTo(x, y + 1)
                  .lineTo(x + dx, y - 1.8 - hash2(i * 4 + j, variant, 491) * 2)
                  .stroke({ width: 1, color: mark(shade(pal.alt, 1.75)), alpha: 0.55 });
              }
            }
            // The stones a plough throws out, lit north-west like everything
            // else on this ground.
            for (let i = 0; i < 4; i++) {
              const x = hash2(i, variant, 493) * S;
              const y = hash2(i, variant, 495) * S;
              const r = 0.8 + hash2(i, variant, 497) * 1.2;
              g.poly([x - r, y + r * 0.5, x - r * 0.3, y - r, x + r, y + r * 0.4]).fill({
                color: mark(shade(pal.alt, 1.85)),
                alpha: 0.55,
              });
            }
            break;
          }
        }
        break;
      }
    }

    // No edge darkening: at close zoom it turned the ground into visible graph
    // paper. Variation has to come from the grain, not from the tile boundary.
    return this.bake(g, S, S);
  }

  // ── Walls ────────────────────────────────────────────────────────────────

  private buildWalls(): void {
    const S = ART_TILE_PX;
    // Bit-mask of connected neighbours: 1=N 2=E 4=S 8=W. Bake all 16.
    for (let mask = 0; mask < 16; mask++) {
      const g = new Graphics();
      const stone = 0x565045;
      const top = 0x6b6558;
      const dark = 0x322d26;

      const core = S * 0.56;
      const half = core / 2;
      const cx = S / 2;
      const cy = S / 2;

      const arm = (dx: number, dy: number) => {
        const x = cx + (dx * S) / 2 - (dx === 0 ? half : dx > 0 ? 0 : (S / 2));
        const y = cy + (dy * S) / 2 - (dy === 0 ? half : dy > 0 ? 0 : (S / 2));
        const w = dx === 0 ? core : S / 2;
        const h = dy === 0 ? core : S / 2;
        g.rect(x, y, w, h).fill(stone);
      };

      g.rect(cx - half, cy - half, core, core).fill(stone);
      if (mask & 1) arm(0, -1);
      if (mask & 2) arm(1, 0);
      if (mask & 4) arm(0, 1);
      if (mask & 8) arm(-1, 0);

      // Crenellations and a lit top edge, so walls read as solid volume.
      g.rect(cx - half, cy - half, core, 3).fill(top);
      g.rect(cx - half, cy + half - 2.5, core, 2.5).fill(dark);
      for (let i = 0; i < 3; i++) {
        g.rect(cx - half + 2 + i * (core / 3), cy - half - 1.5, core / 6, 3).fill(top);
      }
      this.walls[`w${mask}`] = this.bake(g, S, S);
    }

    // Gate: timber leaves with iron bands.
    for (const vertical of [false, true]) {
      const g = new Graphics();
      const w = vertical ? S * 0.6 : S;
      const h = vertical ? S : S * 0.6;
      const x = (S - w) / 2;
      const y = (S - h) / 2;
      g.rect(x, y, w, h).fill(0x4a3a2a);
      for (let i = 0; i < 4; i++) {
        if (vertical) g.rect(x, y + (i * h) / 4, w, 1.6).fill(0x30271d);
        else g.rect(x + (i * w) / 4, y, 1.6, h).fill(0x30271d);
      }
      g.rect(x, y, w, 2.4).fill(0x6b5540);
      g.rect(x, y, w, h).stroke({ width: 2, color: 0x2a2621 });
      g.circle(S / 2, S / 2, 3).fill(0x8a8378);
      this.walls[vertical ? 'gateV' : 'gateH'] = this.bake(g, S, S);
    }

    // Rubble left where a wall used to be.
    const rub = new Graphics();
    for (let i = 0; i < 7; i++) {
      const x = 4 + hash2(i, 3, 13) * (S - 8);
      const y = 4 + hash2(i, 3, 29) * (S - 8);
      const r = 1.6 + hash2(i, 3, 47) * 3;
      rub.poly([x - r, y + r * 0.6, x - r * 0.3, y - r, x + r, y + r * 0.4]).fill(0x4a453d);
    }
    this.walls.rubble = this.bake(rub, S, S);
  }

  // ── Units ────────────────────────────────────────────────────────────────

  private buildUnits(): void {
    for (let i = 0; i < UNIT_TYPES.length; i++) {
      const t = UNIT_TYPES[i];
      const fac = t.faction === 'common' ? undefined : faction(t.faction);
      const accent = fac?.accent ?? C.gold;
      // Everything is lifted well above the ground values: the world is ash, and
      // the only way a soldier reads against ash is to be brighter than it.
      const cloth = shade(fac?.cloth ?? 0x45403a, 1.35);
      const metal = shade(fac?.metal ?? 0x8a8378, 1.12);
      this.units[i] = [
        this.bakeUnit(t, accent, cloth, metal, C.friendlyRim),
        // Invaders: the same build, lit cold. Hue carries the side, value stays
        // high enough that they are just as legible in a night fight.
        this.bakeUnit(
          t,
          mix(accent, 0x8fa6d8, 0.4),
          mix(cloth, 0x54637f, 0.5),
          mix(metal, 0x93a4bb, 0.45),
          C.hostileRim,
        ),
      ];
    }
  }

  private bakeUnit(t: UnitType, accent: number, cloth: number, metal: number, rim: number): UnitArt {
    const S = ART_UNIT_PX;
    const vis = t.vis;
    const p: Palette = { accent, cloth, metal, rim };

    // Engines roll; only legs bob. Baking one frame for them saves a texture per
    // team and the pair simply points at it twice.
    const frameCount = vis.build === 'siege' ? 1 : 2;
    const frames: Texture[] = [];
    for (let f = 0; f < frameCount; f++) {
      const body = new Graphics();
      drawBody(body, vis, p, f);
      const holder = new Container();
      holder.addChild(body);
      holder.setFromMatrix(new Matrix(1, 0, 0, 1, S / 2, S / 2));
      frames.push(this.bake(holder, S, S, 2));
    }
    if (frames.length === 1) frames.push(frames[0]);

    /**
     * The weapon box is sized to the weapon rather than fixed.
     *
     * `scene` turns the pivot into a sprite anchor by dividing by 48, so what it
     * actually consumes is the pivot's *fraction* of the texture — which means
     * the box may be any size as long as the pivot is reported against 48. That
     * matters: under the old fixed 48-pixel box a warlord's axe at scale 1.6 was
     * sawn off at the haft, and a pike could never be longer than a sword.
     */
    const b = WEAPON_BOUNDS[vis.weapon];
    const sd = drawScale(vis.scale);
    const m = 4;
    const px = b ? -b.x0 * sd + m : 10;
    const ws = b
      ? Math.ceil(Math.max(48, px + b.x1 * sd + m, 2 * (Math.max(-b.y0, b.y1) * sd + m)))
      : 48;

    const weaponG = new Graphics();
    drawWeapon(weaponG, vis.weapon, p, sd);
    const wHolder = new Container();
    wHolder.addChild(weaponG);
    wHolder.setFromMatrix(new Matrix(1, 0, 0, 1, px, ws / 2));

    return {
      body: frames[0],
      frames,
      weapon: this.bake(wHolder, ws, ws, 2),
      weaponPivotX: (px / ws) * 48,
      weaponPivotY: 24,
      scale: vis.scale,
    };
  }

  // ── Buildings ────────────────────────────────────────────────────────────

  private bakeBuilding(def: BuildingType, skin: Vernacular): Texture {
    const px = ART_TILE_PX;
    const W = def.w * px;
    const H = def.h * px;
    // Extra headroom above the footprint for the fake-3D roof.
    const pad = Math.round(px * 1.4 * def.vis.height);
    const g = new Graphics();
    drawBuilding(g, def, skin, W, H, pad);
    const holder = new Container();
    holder.addChild(g);
    holder.setFromMatrix(new Matrix(1, 0, 0, 1, 0, pad));
    return this.bake(holder, W, H + pad, 1.5);
  }
}

/** Which war-map token stands in for this unit when the camera pulls back. */
export function tokenIndexFor(flags: number, build: string): number {
  // A boss is a star whatever body it happens to wear. The three Siege bosses
  // are brutes and casters by build, and reading them as "another big one" is
  // exactly the mistake that gets a line eaten.
  if ((flags & UF.Champion) !== 0 || build === 'champion') return 7;
  if (build === 'villager') return 6;
  if (build === 'siege') return 3;
  if (build === 'brute') return 5;
  if (build === 'caster') return 4;
  if (build === 'cavalry') return 2;
  if (build === 'archer') return 1;
  if (build === 'swarm') return 8;
  if (build === 'flier') return 9;
  return 0;
}

// ── Unit silhouettes ───────────────────────────────────────────────────────

/** Near-black. Every silhouette is separated from the ground by a ring of it. */
const INK = 0x0d0b09;

interface Palette {
  accent: number;
  cloth: number;
  metal: number;
  rim: number;
}

/** A path issued onto a Graphics, left unfilled so it can be drawn more than once. */
type Path = (g: Graphics) => void;

/**
 * The three-layer read, in this order and for this reason:
 *   1. a near-black outline, so the shape separates from dark ground
 *   2. a bright team rim, so you know whose it is before you know what it is
 *   3. the body itself, which covers all but a thin ring of the rim
 *
 * Without (1) a 400-unit brawl is grey soup; without (2) you cannot read a front
 * line at a glance. Both were learned by looking at the screenshots.
 *
 * Stroking one path three times, rather than drawing three concentric ellipses,
 * is what lets a build have its own outline instead of every build being an egg.
 */
function silhouette(g: Graphics, path: Path, fill: number, p: Palette, ink = 5.4, rim = 2.5): void {
  path(g);
  g.fill({ color: INK, alpha: 0.94 }).stroke({ width: ink, color: INK, alpha: 0.94, join: 'round' });
  path(g);
  g.fill(p.rim).stroke({ width: rim, color: p.rim, join: 'round' });
  path(g);
  g.fill(fill);
}

/** Ink outline only — for parts that sit inside the outer silhouette. */
function inked(g: Graphics, path: Path, fill: number, ink = 3.0, alpha = 0.9): void {
  path(g);
  g.fill({ color: INK, alpha }).stroke({ width: ink, color: INK, alpha, join: 'round' });
  path(g);
  g.fill(fill);
}

/** Stroke a path twice: a fat ink pass, then the bright pass over it. */
function inkedStroke(g: Graphics, path: Path, width: number, color: number, alpha = 1): void {
  path(g);
  g.stroke({ width: width + 2.8, color: INK, alpha: 0.92, cap: 'round', join: 'round' });
  path(g);
  g.stroke({ width, color, alpha, cap: 'round', join: 'round' });
}

/**
 * Drawing scale, compressed above 1.15.
 *
 * The art box is fixed and `vis.scale` runs to 1.7, which is more than a
 * champion with a cape, a crown of blades and a standard on its back can fit
 * inside. Compressing the top of the range keeps the big models unmistakably
 * bigger without sawing anything off — and nobody on a battlefield can measure
 * the difference between "1.7 times a man" and "1.45 times a man", whereas a
 * clipped banner is something everybody can see.
 *
 * `UnitArt.scale` still reports the *authored* scale, because the shadow and
 * the weapon-hand offset in `scene` are in world units and should not be lied
 * to about how big the model is meant to be.
 */
function drawScale(s: number): number {
  return s <= 1.15 ? s : 1.15 + (s - 1.15) * 0.55;
}

/**
 * Draw a unit facing +x, centred on (0, 0), in an ART_UNIT_PX box.
 * Top-down three-quarter: you see shoulders, the crown of the helm, and the
 * weapon held out to one side. `frame` is 0 or 1 — the walk cycle.
 */
function drawBody(g: Graphics, vis: UnitVisual, p: Palette, frame: number): void {
  const s = drawScale(vis.scale);
  switch (vis.build) {
    case 'cavalry':
      drawCavalry(g, vis, p, s, frame);
      break;
    case 'siege':
      drawSiegeEngine(g, vis, p, s);
      break;
    case 'brute':
      drawBrute(g, vis, p, s, frame);
      break;
    case 'swarm':
      drawSwarm(g, vis, p, s, frame);
      break;
    case 'flier':
      drawFlier(g, vis, p, s, frame);
      break;
    case 'villager':
      drawVillager(g, p, s, frame);
      break;
    case 'champion':
      drawChampion(g, vis, p, s, frame);
      break;
    case 'caster':
      drawCaster(g, vis, p, s, frame);
      break;
    case 'archer':
      drawArcher(g, vis, p, s, frame);
      break;
    default:
      drawInfantry(g, vis, p, s, frame);
      break;
  }
}

// ── Shared humanoid parts ──────────────────────────────────────────────────

/**
 * The walk cycle: two boots, one forward, swapped between frames.
 *
 * You cannot see a leg at tactical zoom. You can see the back of the outline
 * change shape twice a second, and that is the whole point — it is the
 * difference between a company marching and a sheet of decals sliding along.
 * Drawn first so the torso sits over them.
 */
function legs(g: Graphics, p: Palette, s: number, frame: number, back: number, spread: number, r: number): void {
  for (const side of [-1, 1]) {
    const step = (frame === 0 ? side : -side) * 4.0 * s;
    const x = back + step;
    silhouette(g, (gg) => gg.ellipse(x, side * spread, r * 1.3, r * 0.92), shade(p.cloth, 0.5), p, 4.2, 2.0);
  }
}

/**
 * A cape, and it is only worth drawing if it changes the outline — so it flares
 * wider than the shoulders and ends in three ragged points rather than a hem.
 */
function drawCloak(g: Graphics, p: Palette, back: number, sw: number): void {
  const b = back;
  silhouette(
    g,
    (gg) =>
      gg.poly([
        -b * 0.1, -sw * 0.9,
        -b * 1.25, -sw * 1.38,
        -b * 1.95, -sw * 0.76,
        -b * 1.6, -sw * 0.06,
        -b * 2.05, sw * 0.34,
        -b * 1.45, sw * 0.96,
        -b * 0.95, sw * 1.4,
        -b * 0.1, sw * 0.9,
      ]),
    shade(p.cloth, 0.6),
    p,
    4.8,
    2.2,
  );
  g.poly([-b * 0.1, -sw * 0.48, -b * 1.5, -sw * 0.18, -b * 1.35, sw * 0.48, -b * 0.1, sw * 0.48]).fill({
    color: p.accent,
    alpha: 0.24,
  });
}

/**
 * Head and helm.
 *
 * `helm` is a silhouette parameter, not a texture swap: 0 bare, 1 kettle brim,
 * 2 nasal bar, 3 crest, 4 horned great helm, 5 hood. Each number has to change
 * the *outline*, because at tactical zoom the outline is all that survives, and
 * a helmet that only changes the fill is a helmet nobody will ever see.
 */
function drawHead(g: Graphics, vis: UnitVisual, p: Palette, hx: number, r: number): void {
  // The head is the brightest thing on the model on purpose. It is small, it is
  // surrounded by armour of similar hue, and if it does not out-value the
  // pauldrons it disappears — which costs you the one cue that says which way
  // the man is facing.
  const steel = shade(p.metal, 1.5);
  const steelDim = shade(p.metal, 0.72);
  const helm = vis.helm;

  if (helm === 5) {
    // A hood: a long cowl with the peak thrown back, and one lit slot for a face.
    silhouette(
      g,
      (gg) =>
        gg.poly([
          hx + r * 1.15, 0,
          hx + r * 0.1, -r * 1.5,
          hx - r * 2.0, -r * 0.85,
          hx - r * 2.5, 0,
          hx - r * 2.0, r * 0.85,
          hx + r * 0.1, r * 1.5,
        ]),
      shade(p.cloth, 0.62),
      p,
      4.4,
      2.0,
    );
    g.poly([hx + r * 0.9, 0, hx - r * 0.3, -r * 0.7, hx - r * 0.3, r * 0.7]).fill(0x14120f);
    g.circle(hx + r * 0.35, 0, r * 0.3).fill({ color: p.accent, alpha: 0.95 });
    return;
  }

  if (helm === 1) {
    // Kettle hat. Offset backwards rather than concentric: a ring of dark
    // around a bright skull reads as an eyeball, which is not the intention.
    inked(g, (gg) => gg.ellipse(hx - r * 0.5, 0, r * 1.2, r * 1.5), steelDim, 3.2);
  }

  inked(g, (gg) => gg.circle(hx, 0, r), helm > 0 ? steel : 0xc8ae8e, 3.4);
  // Offset on both axes, so the shading is a crescent and not a second ring —
  // concentric circles made every helmeted head read as a target.
  g.circle(hx - r * 0.42, r * 0.3, r * 0.66).fill(helm > 0 ? steelDim : 0x8d7a63);

  if (helm === 2) {
    // Nasal bar: a single spike forward of the face. Cheap, and it points.
    inked(g, (gg) => gg.poly([hx + r * 1.9, 0, hx - r * 0.2, -r * 0.42, hx - r * 0.2, r * 0.42]), steel, 2.4);
  }
  if (helm === 3) {
    // Crest, running fore-and-aft and overhanging both ends of the skull.
    inked(
      g,
      (gg) =>
        gg.poly([
          hx + r * 1.7, 0,
          hx + r * 0.2, -r * 0.34,
          hx - r * 1.1, -r * 0.5,
          hx - r * 2.0, -r * 0.2,
          hx - r * 1.9, r * 0.2,
          hx - r * 1.1, r * 0.5,
          hx + r * 0.2, r * 0.34,
        ]),
      p.accent,
      2.6,
    );
  }
  if (helm === 4) {
    // A horned great helm. Two sweeps of bone off the sides: the single most
    // recognisable outline a head can have at eight pixels across.
    for (const side of [-1, 1]) {
      silhouette(
        g,
        (gg) =>
          gg.poly([
            hx + r * 0.1, side * r * 0.8,
            hx - r * 1.5, side * r * 2.5,
            hx - r * 2.4, side * r * 2.0,
            hx - r * 0.9, side * r * 0.75,
          ]),
        C.boneDim,
        p,
        3.6,
        1.6,
      );
    }
    g.rect(hx + r * 0.15, -r * 0.55, r * 0.8, r * 1.1).fill(0x14120f);
  }
}

/**
 * Pauldrons — the WoW rule: shoulders are half the silhouette. Angular, not
 * round, so they read as armour rather than as more blob; and at 2 they grow a
 * spike that clears the body outline, which is what makes heavy infantry
 * distinguishable from light at a glance.
 */
function drawPauldrons(g: Graphics, vis: UnitVisual, p: Palette, s: number, fx: number, sw: number): void {
  if (vis.pauldron <= 0) return;
  const pr = (3.6 + vis.pauldron * 2.0) * s;
  const plate = shade(p.metal, 0.66);
  for (const side of [-1, 1]) {
    const py = side * (sw - pr * 0.3);
    // Wide enough in x to cap the whole shoulder. A narrow plate reads as a bar
    // laid across the man rather than as armour he is wearing.
    inked(
      g,
      (gg) =>
        gg.poly([
          fx * 0.8, py - side * pr * 0.3,
          fx * 0.2, py - side * pr * 0.5,
          -fx * 0.82, py - side * pr * 0.38,
          -fx * 0.92, py + side * pr * 0.62,
          fx * 0.72, py + side * pr * 0.8,
        ]),
      plate,
      3.0,
    );
    g.moveTo(fx * 0.8, py - side * pr * 0.3)
      .lineTo(-fx * 0.82, py - side * pr * 0.38)
      .stroke({ width: 1.7 * s, color: shade(p.metal, 1.2) });
    if (vis.pauldron > 1) {
      // The spike. It breaks the outline on purpose — but in steel, with only
      // its edge in the faction colour: a solid accent spike at this size
      // turned every heavy infantryman into a pair of coloured wings.
      const spike: Path = (gg) =>
        gg.poly([
          -fx * 0.15, py + side * pr * 0.5,
          -fx * 0.95, py + side * pr * 1.22,
          fx * 0.45, py + side * pr * 0.9,
        ]);
      silhouette(g, spike, shade(p.metal, 1.05), p, 3.4, 1.5);
      spike(g);
      g.stroke({ width: 1.1 * s, color: p.accent, alpha: 0.9 });
    }
  }
}

/** A kite shield, held out clear of the body edge so it widens the outline. */
function drawShield(g: Graphics, p: Palette, s: number, fx: number, sw: number): void {
  const sx = fx * 0.5;
  const sy = -sw * 1.05;
  const path: Path = (gg) =>
    gg.poly([
      sx + 5.4 * s, sy - 1.4 * s,
      sx + 0.4 * s, sy - 7.4 * s,
      sx - 5.2 * s, sy - 2.2 * s,
      sx - 4.4 * s, sy + 5.2 * s,
      sx + 1.0 * s, sy + 7.6 * s,
      sx + 5.2 * s, sy + 4.0 * s,
    ]);
  silhouette(g, path, shade(p.metal, 0.85), p, 4.6, 2.0);
  g.poly([
    sx + 3.0 * s, sy - 1.0 * s,
    sx + 0.3 * s, sy - 4.6 * s,
    sx - 3.0 * s, sy - 1.4 * s,
    sx - 2.6 * s, sy + 3.0 * s,
    sx + 0.6 * s, sy + 4.6 * s,
    sx + 3.0 * s, sy + 2.4 * s,
  ]).fill(p.accent);
  g.circle(sx, sy, 1.5 * s).fill(shade(p.metal, 1.5));
}

/**
 * A standard, carried on the back and angled off the left shoulder.
 *
 * Long enough that it clears every other man in the rank — a banner that does
 * not break the skyline of a company cannot do the one job a banner has.
 */
function drawBanner(g: Graphics, p: Palette, s: number, back: number, sw: number): void {
  const bx = -back * 0.6;
  const by = sw * 0.5;
  const tx = -back * 1.6;
  // Clamped, not proportional: a brute is three times a man across the
  // shoulders, but its standard is a pole of the same length, and scaling one
  // off the other pushed the pennant clean out of the texture.
  const ty = Math.min(sw * 1.75, 19 * s);
  inkedStroke(g, (gg) => gg.moveTo(bx, by).lineTo(tx, ty), 2.2 * s, 0x4a4036);
  const flag: Path = (gg) =>
    gg.poly([
      tx, ty,
      tx + 8.4 * s, ty + 2.6 * s,
      tx + 5.6 * s, ty - 2.2 * s,
      tx + 9.6 * s, ty - 5.6 * s,
      tx + 1.0 * s, ty - 5.0 * s,
    ]);
  silhouette(g, flag, p.accent, p, 4.0, 1.8);
  g.circle(tx, ty, 1.6 * s).fill(shade(p.metal, 1.4));
}

// ── Builds ────────────────────────────────────────────────────────────────

/**
 * Line infantry: the baseline every other build is read against.
 *
 * Hard, square shoulders and a chest that juts forward — a slab, not an egg.
 * Everything else in the catalogue is defined by how it differs from this.
 */
function drawInfantry(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  const sw = (10.6 + vis.pauldron * 1.6) * s;
  const fx = 9.4 * s;
  const bx = 9.8 * s;

  legs(g, p, s, frame, -bx * 0.9, sw * 0.78, 3.5 * s);
  if (vis.cloak) drawCloak(g, p, bx, sw);

  const torso: Path = (gg) =>
    gg.poly([
      fx, -sw * 0.5,
      fx * 0.6, -sw,
      -bx * 0.7, -sw * 0.92,
      -bx, -sw * 0.3,
      -bx, sw * 0.3,
      -bx * 0.7, sw * 0.92,
      fx * 0.6, sw,
      fx, sw * 0.5,
    ]);
  silhouette(g, torso, shade(p.cloth, 1.08), p);
  // Lit from the front: the leading edge catches light and the trailing edge
  // falls away, and that gradient is most of the sense of volume.
  g.poly([0, -sw * 0.78, -bx * 0.7, -sw * 0.92, -bx, -sw * 0.3, -bx, sw * 0.3, -bx * 0.7, sw * 0.92, 0, sw * 0.78]).fill(
    shade(p.cloth, 0.7),
  );
  // Faction surcoat across the chest — the one saturated thing on the model.
  // A band standing up the shoulder axis, not a slab down the length of him:
  // filled the other way round it swallowed the whole torso and every Ashen
  // soldier came out the colour of his own banner.
  g.poly([
    fx * 0.74, -sw * 0.7,
    fx * 0.2, -sw * 0.88,
    -bx * 0.3, -sw * 0.8,
    -bx * 0.36, sw * 0.8,
    fx * 0.2, sw * 0.88,
    fx * 0.74, sw * 0.7,
  ]).fill({ color: p.accent, alpha: 0.8 });

  drawPauldrons(g, vis, p, s, fx, sw);
  drawHead(g, vis, p, fx * 1.14, 4.6 * s);
  if (vis.shield) drawShield(g, p, s, fx, sw);
  if (vis.banner) drawBanner(g, p, s, bx, sw);
}

/**
 * Archers stand bladed: lead shoulder thrown forward, rear shoulder pulled
 * back, quiver breaking the outline behind. Narrow across, long fore-and-aft —
 * the opposite proportion to the line, which is what stops a bowman ever
 * reading as a short spearman.
 */
function drawArcher(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  const sw = (8.0 + vis.pauldron * 1.2) * s;
  const fx = 10.6 * s;
  const bx = 8.8 * s;

  legs(g, p, s, frame, -bx * 0.7, sw * 0.44, 3.1 * s);
  if (vis.cloak) drawCloak(g, p, bx, sw);

  const torso: Path = (gg) =>
    gg.poly([
      fx, -sw * 0.18,
      fx * 0.52, -sw * 1.14,
      -bx * 0.42, -sw * 0.95,
      -bx, -sw * 0.12,
      -bx * 0.74, sw * 0.84,
      fx * 0.16, sw * 0.9,
      fx * 0.88, sw * 0.4,
    ]);
  silhouette(g, torso, shade(p.cloth, 1.02), p);
  g.poly([-bx * 0.42, -sw * 0.95, -bx, -sw * 0.12, -bx * 0.74, sw * 0.84, 0, sw * 0.6, 0, -sw * 0.75]).fill(
    shade(p.cloth, 0.68),
  );
  g.poly([fx * 0.86, -sw * 0.2, fx * 0.4, -sw * 0.62, -bx * 0.3, -sw * 0.3, -bx * 0.2, sw * 0.3, fx * 0.6, sw * 0.4]).fill(
    { color: p.accent, alpha: 0.78 },
  );

  // The quiver: a tube over the back shoulder with three fletchings clear of
  // the body. It is the archer's badge, and it costs four polygons.
  const qx = -bx * 0.55;
  const qy = sw * 0.7;
  silhouette(
    g,
    (gg) =>
      gg.poly([
        qx + 2.2 * s, qy - 1.6 * s,
        qx - 4.6 * s, qy + 4.4 * s,
        qx - 2.0 * s, qy + 7.0 * s,
        qx + 4.6 * s, qy + 1.2 * s,
      ]),
    0x4a3a2a,
    p,
    3.8,
    1.7,
  );
  for (let i = -1; i <= 1; i++) {
    inkedStroke(
      g,
      (gg) =>
        gg
          .moveTo(qx - 3.4 * s + i * 1.5 * s, qy + 5.4 * s + i * 0.6 * s)
          .lineTo(qx - 7.4 * s + i * 1.6 * s, qy + 9.0 * s + i * 0.8 * s),
      1.5 * s,
      C.boneDim,
    );
  }

  drawPauldrons(g, vis, p, s, fx, sw);
  drawHead(g, vis, p, fx * 1.0, 4.2 * s);
  if (vis.shield) drawShield(g, p, s, fx, sw);
  if (vis.banner) drawBanner(g, p, s, bx, sw);
}

/**
 * Cavalry is the mount, not the rider.
 *
 * Two and a half times as long as it is wide, with a neck thrown forward and a
 * tail behind: at tactical zoom a horseman has to be a *shape*, not a soldier
 * with a bigger disc. Four legs carry the walk cycle, which at a canter is the
 * only thing on the field that visibly moves faster than the men.
 */
function drawCavalry(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  const L = 15.5 * s;
  const W = 6.6 * s;

  // Legs: fore pair and hind pair, in diagonal pairs, so the gait reads.
  for (const fore of [-1, 1]) {
    for (const side of [-1, 1]) {
      const swing = (frame === 0 ? fore * side : -fore * side) * 3.0 * s;
      inked(
        g,
        (gg) => gg.ellipse(L * 0.52 * fore + swing, side * W * 1.02, 3.0 * s, 2.1 * s),
        0x2b241d,
        2.6,
      );
    }
  }
  // Tail.
  inked(
    g,
    (gg) => gg.poly([-L * 0.85, -W * 0.4, -L * 1.5, -W * 0.95, -L * 1.28, 0, -L * 1.45, W * 0.9, -L * 0.85, W * 0.4]),
    0x312a22,
    2.8,
  );

  // Barrel, neck and head as one path — one animal, not a horse with a lump.
  const beast: Path = (gg) =>
    gg.poly([
      -L, -W * 0.55,
      -L * 0.55, -W,
      L * 0.34, -W * 0.98,
      L * 0.7, -W * 0.6,
      L * 1.24, -W * 0.5,
      L * 1.52, -W * 0.26,
      L * 1.48, W * 0.2,
      L * 1.18, W * 0.42,
      L * 0.68, W * 0.58,
      L * 0.3, W * 0.98,
      -L * 0.55, W,
      -L, W * 0.55,
    ]);
  silhouette(g, beast, 0x5b4d40, p);
  g.poly([-L, -W * 0.55, -L * 0.55, -W, L * 0.34, -W * 0.98, L * 0.3, 0, -L, 0]).fill(0x453a30);
  g.circle(L * 1.42, 0, W * 0.3).fill(0x2a231d);
  // Barding, in the faction colour, over the hindquarters.
  g.poly([-L * 0.1, -W * 0.98, -L * 0.86, -W * 0.78, -L * 0.92, W * 0.78, -L * 0.1, W * 0.98]).fill({
    color: p.accent,
    alpha: 0.55,
  });

  // The rider, sat forward of centre and small — the size contrast is what
  // makes the horse read as a horse.
  const rs = s * 0.78;
  const rx = -L * 0.06;
  const rsw = (7.6 + vis.pauldron * 1.4) * rs;
  if (vis.cloak) {
    silhouette(
      g,
      (gg) =>
        gg.poly([
          rx, -rsw * 0.9,
          rx - 9.0 * rs, -rsw * 1.5,
          rx - 13.5 * rs, -rsw * 0.4,
          rx - 11.0 * rs, rsw * 0.6,
          rx - 8.0 * rs, rsw * 1.5,
          rx, rsw * 0.9,
        ]),
      shade(p.cloth, 0.6),
      p,
      4.2,
      1.9,
    );
  }
  const rider: Path = (gg) =>
    gg.poly([
      rx + 7.4 * rs, -rsw * 0.45,
      rx + 4.4 * rs, -rsw,
      rx - 6.0 * rs, -rsw * 0.9,
      rx - 7.6 * rs, 0,
      rx - 6.0 * rs, rsw * 0.9,
      rx + 4.4 * rs, rsw,
      rx + 7.4 * rs, rsw * 0.45,
    ]);
  silhouette(g, rider, shade(p.cloth, 1.12), p, 4.8, 2.2);
  g.poly([rx + 6.6 * rs, -rsw * 0.32, rx + 1.4 * rs, -rsw * 0.5, rx - 5.0 * rs, -rsw * 0.2, rx - 5.0 * rs, rsw * 0.2, rx + 1.4 * rs, rsw * 0.5, rx + 6.6 * rs, rsw * 0.32]).fill(
    { color: p.accent, alpha: 0.85 },
  );
  drawPauldrons(g, vis, p, rs, rx + 7.4 * rs, rsw);
  drawHead(g, vis, p, rx + 7.6 * rs, 4.2 * rs);
  if (vis.shield) drawShield(g, p, rs, rx + 7.4 * rs, rsw);
  if (vis.banner) drawBanner(g, p, rs, 8.0 * rs, rsw);
}

/**
 * A brute is an anvil: two colossal shoulder masses, arms hanging forward past
 * the chest, and a head so small and so sunk between them that the scale reads
 * without a reference. It must never share an outline with infantry, so it has
 * none of infantry's straight lines.
 */
function drawBrute(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  const sw = 15.0 * s;
  const fx = 10.4 * s;
  const bx = 10.0 * s;

  legs(g, p, s, frame, -bx * 0.62, sw * 0.52, 5.0 * s);

  // The hump: a hunched back that pushes the mass behind the shoulders.
  inked(g, (gg) => gg.ellipse(-bx * 0.5, 0, bx * 0.9, sw * 0.72), shade(p.cloth, 0.72), 3.4);

  const torso: Path = (gg) =>
    gg.poly([
      fx * 0.88, -sw * 0.36,
      fx * 0.5, -sw * 0.88,
      -bx * 0.2, -sw,
      -bx * 0.95, -sw * 0.5,
      -bx * 1.05, sw * 0.5,
      -bx * 0.2, sw,
      fx * 0.5, sw * 0.88,
      fx * 0.88, sw * 0.36,
    ]);
  silhouette(g, torso, shade(p.cloth, 1.45), p, 6.4, 3.0);
  g.ellipse(-bx * 0.28, 0, bx * 0.72, sw * 0.78).fill(shade(p.cloth, 1.02));

  // Shoulder lumps and the arms that hang off them, drawn as one mass per side.
  // Separate circles made the thing read as a pile of balls; a shoulder that
  // runs into an arm that ends in a fist reads as a limb.
  for (const side of [-1, 1]) {
    const swing = (frame === 0 ? -side : side) * 2.2 * s;
    silhouette(
      g,
      (gg) =>
        gg.poly([
          -bx * 0.55, side * sw * 0.6,
          -bx * 0.62, side * sw * 1.06,
          -bx * 0.05, side * sw * 1.24,
          fx * 0.7 + swing, side * sw * 1.14,
          fx * 1.24 + swing, side * sw * 0.66,
          fx * 0.9 + swing, side * sw * 0.3,
          fx * 0.1, side * sw * 0.46,
        ]),
      shade(p.cloth, 1.28),
      p,
      5.4,
      2.4,
    );
    g.poly([
      -bx * 0.5, side * sw * 0.66,
      -bx * 0.55, side * sw * 1.0,
      fx * 0.2, side * sw * 1.02,
      fx * 0.1, side * sw * 0.56,
    ]).fill(shade(p.metal, 0.95));
    // The fist, and it is the fist that swings.
    inked(g, (gg) => gg.ellipse(fx * 1.0 + swing, side * sw * 0.8, 4.6 * s, 3.6 * s), shade(p.cloth, 1.4), 3.2);
  }

  // Small head, sunk between them — the size contrast is what sells the scale.
  drawHead(g, vis, p, fx * 1.02, 4.4 * s);
  if (vis.banner) drawBanner(g, p, s, bx, sw);
}

/**
 * Casters have no shoulders to speak of: a robe is a teardrop, narrow at the
 * front and flaring to a wide hem behind, with a hood peak on top. Long and
 * thin where infantry is short and wide.
 */
function drawCaster(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  const fx = 8.0 * s;
  const bx = 12.0 * s;
  const sw = 9.0 * s;
  // A hem does not lift its feet, so the bob is in the hem itself: the trailing
  // corner swings, and that is enough movement to stop the robe looking pinned.
  const sway = (frame === 0 ? 1 : -1) * 1.6 * s;

  const robe: Path = (gg) =>
    gg.poly([
      fx, -sw * 0.28,
      fx * 0.35, -sw * 0.86,
      -bx * 0.45, -sw * 1.02 + sway,
      -bx * 0.95, -sw * 0.5,
      -bx, sw * 0.16,
      -bx * 0.8, sw * 0.86 - sway,
      -bx * 0.2, sw * 1.02,
      fx * 0.4, sw * 0.82,
      fx * 0.95, sw * 0.3,
    ]);
  silhouette(g, robe, shade(p.cloth, 1.22), p);
  g.poly([-bx * 0.45, -sw * 1.02 + sway, -bx * 0.95, -sw * 0.5, -bx, sw * 0.16, -bx * 0.8, sw * 0.86 - sway, -bx * 0.1, sw * 0.5, -bx * 0.1, -sw * 0.5]).fill(
    shade(p.cloth, 0.76),
  );
  // A stole down the front, which is where the faction colour goes on a robe.
  g.poly([fx * 0.92, -sw * 0.2, fx * 0.2, -sw * 0.34, -bx * 0.7, -sw * 0.24, -bx * 0.7, sw * 0.24, fx * 0.2, sw * 0.34, fx * 0.92, sw * 0.2]).fill(
    { color: p.accent, alpha: 0.35 },
  );
  // Sleeves: two narrow cuffs thrown forward, so the arms exist in outline.
  for (const side of [-1, 1]) {
    inked(
      g,
      (gg) =>
        gg.poly([
          fx * 0.2, side * sw * 0.62,
          fx * 1.05, side * sw * 0.86,
          fx * 1.2, side * sw * 0.42,
          fx * 0.4, side * sw * 0.24,
        ]),
      shade(p.cloth, 0.9),
      2.8,
    );
  }

  drawPauldrons(g, vis, p, s, fx, sw);
  // A caster's head is a hood whatever `helm` says short of a helmet: 0-2 all
  // read as cowls, because a bare-headed sorcerer is a man in a dressing gown.
  drawHead(g, { ...vis, helm: vis.helm >= 3 ? vis.helm : 5 }, p, fx * 0.72, 4.6 * s);
  if (vis.banner) drawBanner(g, p, s, bx * 0.7, sw);
}

/**
 * Swarm: low, small, spiky. Four splayed limbs and a snout thrust forward.
 * Rats and wretches must never read as small infantry — they read as vermin,
 * which means no straight lines and no shoulders at all.
 */
function drawSwarm(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  const fx = 7.4 * s;
  const bx = 7.0 * s;
  const sw = 5.6 * s;

  for (const fore of [-1, 1]) {
    for (const side of [-1, 1]) {
      const swing = (frame === 0 ? fore * side : -fore * side) * 2.6 * s;
      inkedStroke(
        g,
        (gg) =>
          gg
            .moveTo(fore * bx * 0.4, side * sw * 0.5)
            .lineTo(fore * bx * 0.8 + swing, side * sw * 1.8),
        1.9 * s,
        shade(p.cloth, 0.7),
      );
    }
  }
  // A tail, because half of what makes a rat a rat is behind it.
  inkedStroke(
    g,
    (gg) => gg.moveTo(-bx * 0.7, 0).quadraticCurveTo(-bx * 1.6, sw * (frame === 0 ? 1.1 : -1.1), -bx * 2.2, 0),
    1.5 * s,
    shade(p.cloth, 0.6),
  );

  const body: Path = (gg) =>
    gg.poly([
      fx * 1.05, 0,
      fx * 0.4, -sw * 0.86,
      -bx * 0.5, -sw,
      -bx, -sw * 0.3,
      -bx * 0.9, sw * 0.5,
      -bx * 0.1, sw,
      fx * 0.55, sw * 0.78,
    ]);
  silhouette(g, body, shade(p.cloth, 1.15), p, 4.6, 2.1);
  g.poly([-bx * 0.5, -sw, -bx, -sw * 0.3, -bx * 0.9, sw * 0.5, -bx * 0.1, sw, 0, 0]).fill(shade(p.cloth, 0.76));
  g.poly([fx * 1.0, 0, fx * 0.3, -sw * 0.5, fx * 0.3, sw * 0.5]).fill({ color: p.accent, alpha: 0.6 });
  // Snout and two pinprick eyes.
  inked(g, (gg) => gg.poly([fx * 1.7, 0, fx * 0.7, -sw * 0.36, fx * 0.7, sw * 0.36]), 0x8f7c62, 2.4);
  for (const side of [-1, 1]) {
    g.circle(fx * 0.85, side * sw * 0.34, 0.9 * s).fill({ color: p.accent, alpha: 0.95 });
  }
  if (vis.helm > 0) {
    inked(g, (gg) => gg.ellipse(fx * 0.5, 0, 3.0 * s, 3.6 * s), shade(p.metal, 1.2), 2.4);
  }
  if (vis.banner) drawBanner(g, p, s, bx, sw);
}

/**
 * Fliers are wings. A wide swept chevron with a small body slung under it —
 * nothing else on the field is wider than it is long, which is the whole trick.
 */
function drawFlier(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  const span = (frame === 0 ? 15.5 : 13.0) * s;
  const fx = 8.0 * s;
  const bx = 8.5 * s;

  for (const side of [-1, 1]) {
    silhouette(
      g,
      (gg) =>
        gg.poly([
          fx * 0.3, side * 2.4 * s,
          fx * 0.1, side * span,
          -bx * 0.7, side * span * 0.92,
          -bx * 1.15, side * span * 0.42,
          -bx * 0.5, side * 3.0 * s,
        ]),
      shade(p.cloth, 0.86),
      p,
      5.0,
      2.2,
    );
    for (let i = 1; i <= 2; i++) {
      g.moveTo(fx * 0.2, side * (2.6 * s + i * 1.2 * s))
        .lineTo(-bx * (0.5 + i * 0.24), side * span * (0.5 + i * 0.2))
        .stroke({ width: 1.2, color: INK, alpha: 0.55 });
    }
  }

  const body: Path = (gg) =>
    gg.poly([fx * 1.1, 0, fx * 0.3, -4.2 * s, -bx * 0.8, -3.4 * s, -bx * 1.2, 0, -bx * 0.8, 3.4 * s, fx * 0.3, 4.2 * s]);
  silhouette(g, body, shade(p.cloth, 1.15), p, 4.8, 2.2);
  g.poly([fx * 1.0, 0, fx * 0.2, -2.4 * s, -bx * 0.6, -2.0 * s, -bx * 0.6, 2.0 * s, fx * 0.2, 2.4 * s]).fill({
    color: p.accent,
    alpha: 0.7,
  });
  drawHead(g, vis, p, fx * 1.15, 3.6 * s);
  if (vis.banner) drawBanner(g, p, s, bx, span * 0.5);
}

/**
 * Villagers: small, round, unarmoured, leaning into the work, with a bundle on
 * the back. They are the size reference for the whole army — if a militiaman
 * does not look twice the man a villager is, the militiaman is drawn wrong.
 */
function drawVillager(g: Graphics, p: Palette, s: number, frame: number): void {
  const fx = 6.4 * s;
  const bx = 6.8 * s;
  const sw = 6.2 * s;

  legs(g, p, s, frame, -bx * 0.6, sw * 0.42, 2.4 * s);
  // The bundle. It is what they are for.
  inked(g, (gg) => gg.ellipse(-bx * 1.05, sw * 0.25, 4.6 * s, 3.8 * s), 0x5a4c38, 2.8);

  silhouette(
    g,
    (gg) => gg.ellipse(0, 0, bx, sw),
    0x6b5f4f,
    p,
    4.4,
    2.0,
  );
  g.ellipse(-bx * 0.3, 0, bx * 0.66, sw * 0.76).fill(0x554b3e);
  inked(g, (gg) => gg.circle(fx * 1.05, 0, 3.4 * s), 0xb9a184, 2.6);
}

/**
 * A champion should be obviously a champion from across the field: bigger than
 * anything but a brute, wearing a cape that flares past the shoulders, a crown
 * of blades, a standard on the back, and a horned helm whatever the data says.
 * The aura ring is the last resort — it works even when the model is nine
 * pixels tall.
 */
function drawChampion(g: Graphics, vis: UnitVisual, p: Palette, s: number, frame: number): void {
  g.circle(0, 0, 19 * s).fill({ color: p.accent, alpha: 0.055 });
  g.circle(0, 0, 19 * s).stroke({ width: 1.2, color: p.accent, alpha: 0.26 });

  const heavy: UnitVisual = {
    ...vis,
    pauldron: 2,
    cloak: true,
    helm: vis.helm === 5 ? 5 : 4,
  };
  drawInfantry(g, heavy, p, s, frame);

  // A crown of blades around the shoulders: five spikes that put a hard,
  // radiating edge on the silhouette nothing else in the game has. In steel
  // rather than in the faction colour — seven solid accent blades turned a
  // warlord into a sunburst and lost the man inside it.
  for (let i = 0; i < 5; i++) {
    const a = -1.05 + (i / 4) * 2.1;
    const r = 13.5 * s;
    const c = Math.cos(a);
    const sn = Math.sin(a);
    const blade: Path = (gg) =>
      gg.poly([
        c * r - sn * 2.0 * s, sn * r + c * 2.0 * s,
        c * (r + 6.4 * s), sn * (r + 6.4 * s),
        c * r + sn * 2.0 * s, sn * r - c * 2.0 * s,
      ]);
    silhouette(g, blade, shade(p.metal, 1.1), p, 3.2, 1.4);
    blade(g);
    g.stroke({ width: 1.3 * s, color: p.accent, alpha: 0.8 });
  }
  // Every champion carries a standard, whether the data asked for one or not:
  // a named enemy that the eye cannot find in a melee is a wasted boss.
  if (!vis.banner) drawBanner(g, p, s, 9.8 * s, 12.2 * s);
}

/**
 * Siege engines. Three quite different machines share the build, chosen by what
 * the crew is holding, because a bolt-thrower and a gate-ram have nothing in
 * common but wheels:
 *   - a hammer → an armoured walker, tall and boxy with an arm over the front
 *   - big and empty-handed → a covered ram, long, roofed, with a head out front
 *   - anything else → a bolt-thrower, whose crossed limbs make a T you can read
 *     from the far side of the map
 */
function drawSiegeEngine(g: Graphics, vis: UnitVisual, p: Palette, s: number): void {
  const kind = vis.weapon === 'hammer' ? 'walker' : vis.scale >= 1.4 ? 'ram' : 'bolt';
  const timber = 0x4a3c2c;
  const timberLit = 0x63513a;
  const iron = 0x6a6459;

  if (kind === 'bolt') {
    const L = 12.5 * s;
    const W = 5.0 * s;
    // The limbs: a great crossbow lying across the chassis.
    inkedStroke(g, (gg) => gg.moveTo(L * 0.5, -W * 3.1).lineTo(L * 0.72, 0).lineTo(L * 0.5, W * 3.1), 3.6 * s, iron);
    inkedStroke(
      g,
      (gg) => gg.moveTo(L * 0.5, -W * 3.1).lineTo(-L * 0.15, 0).lineTo(L * 0.5, W * 3.1),
      1.4 * s,
      C.boneDim,
      0.8,
    );
    silhouette(g, (gg) => gg.poly([L * 1.5, -W * 0.5, -L, -W * 1.15, -L * 1.15, W * 1.15, L * 1.5, W * 0.5]), timber, p, 5.4, 2.4);
    g.poly([L * 1.5, -W * 0.5, -L, -W * 1.15, -L, 0, L * 1.5, 0]).fill(timberLit);
    g.rect(-L * 0.2, -W * 0.5, L * 1.6, W).fill({ color: iron, alpha: 0.9 });
    g.poly([L * 1.9, 0, L * 1.3, -W * 0.34, L * 1.3, W * 0.34]).fill(C.boneDim);
    for (const side of [-1, 1]) {
      inked(g, (gg) => gg.ellipse(-L * 0.55, side * W * 1.25, 3.6 * s, 2.4 * s), 0x2b241c, 2.6);
    }
    g.poly([L * 1.5, -W * 0.5, -L, -W * 1.15, -L * 1.15, W * 1.15, L * 1.5, W * 0.5]).stroke({
      width: 1.5,
      color: p.accent,
      alpha: 0.5,
    });
    return;
  }

  if (kind === 'walker') {
    const L = 11.0 * s;
    const W = 9.0 * s;
    // Legs first, splayed wide — this thing walks, and it should look like it.
    for (const side of [-1, 1]) {
      inked(g, (gg) => gg.poly([-L * 0.2, side * W * 0.7, -L * 1.1, side * W * 1.5, -L * 0.3, side * W * 1.5]), 0x2f2820, 3.0);
      inked(g, (gg) => gg.poly([L * 0.4, side * W * 0.7, L * 1.0, side * W * 1.5, L * 0.1, side * W * 1.5]), 0x2f2820, 3.0);
    }
    silhouette(
      g,
      (gg) => gg.poly([L * 1.15, -W * 0.55, L * 0.5, -W, -L, -W * 0.9, -L * 1.25, 0, -L, W * 0.9, L * 0.5, W, L * 1.15, W * 0.55]),
      shade(p.metal, 0.78),
      p,
      6.2,
      2.8,
    );
    g.poly([L * 1.15, -W * 0.55, L * 0.5, -W, -L, -W * 0.9, -L * 0.2, 0, L * 0.6, 0]).fill(shade(p.metal, 1.0));
    // The arm, hauled back over the shoulder of the machine.
    inkedStroke(g, (gg) => gg.moveTo(-L * 0.4, -W * 0.2).lineTo(L * 1.5, -W * 0.6), 3.4 * s, iron);
    inked(g, (gg) => gg.rect(L * 1.3, -W * 1.15, 6.0 * s, 11.0 * s), shade(p.metal, 1.15), 3.2);
    // A furnace mouth, because Karrun-Dûr does not build anything cold.
    g.circle(-L * 0.55, 0, 3.4 * s).fill({ color: C.emberDeep, alpha: 0.85 });
    g.circle(-L * 0.55, 0, 1.9 * s).fill({ color: C.emberLight, alpha: 0.95 });
    g.poly([L * 1.15, -W * 0.55, L * 0.5, -W, -L, -W * 0.9, -L * 1.25, 0, -L, W * 0.9, L * 0.5, W, L * 1.15, W * 0.55]).stroke(
      { width: 1.6, color: p.accent, alpha: 0.5 },
    );
    return;
  }

  // A covered ram: long, roofed, with the head out in front and nothing else.
  const L = 10.5 * s;
  const W = 8.0 * s;
  for (const dx of [-0.62, 0.5]) {
    for (const side of [-1, 1]) {
      inked(g, (gg) => gg.ellipse(L * dx, side * W * 1.12, 4.0 * s, 2.6 * s), 0x2b241c, 2.8);
    }
  }
  silhouette(
    g,
    (gg) => gg.poly([L * 1.02, -W * 0.62, L * 0.55, -W, -L, -W * 0.92, -L * 1.06, 0, -L, W * 0.92, L * 0.55, W, L * 1.02, W * 0.62]),
    timber,
    p,
    6.0,
    2.7,
  );
  // Planking along the roof, and a ridge beam that says which way it faces.
  for (let i = -2; i <= 2; i++) {
    g.rect(-L * 0.98, i * W * 0.34 - 0.9, L * 1.95, 1.8).fill({ color: 0x33291d, alpha: 0.8 });
  }
  g.poly([L * 1.0, -W * 0.4, -L * 0.98, -W * 0.55, -L * 0.98, 0, L * 1.0, 0]).fill(timberLit);
  // The head.
  inkedStroke(g, (gg) => gg.moveTo(-L * 0.2, 0).lineTo(L * 1.45, 0), 4.0 * s, iron);
  inked(g, (gg) => gg.poly([L * 2.0, 0, L * 1.3, -W * 0.42, L * 1.35, W * 0.42]), shade(p.metal, 1.0), 3.2);
  g.poly([L * 1.02, -W * 0.62, L * 0.55, -W, -L, -W * 0.92, -L * 1.06, 0, -L, W * 0.92, L * 0.55, W, L * 1.02, W * 0.62]).stroke(
    { width: 1.6, color: p.accent, alpha: 0.5 },
  );
}

// ── Weapons ───────────────────────────────────────────────────────────────

/**
 * How far each weapon reaches from the hand, in authoring units, before scale.
 *
 * Used to cut a texture that actually fits the weapon — see `bakeUnit`. Keep
 * these honest: they are the bounding box, outline included, and a weapon that
 * draws outside its entry gets its tip shaved off.
 */
const WEAPON_BOUNDS: Partial<Record<VisWeapon, { x0: number; x1: number; y0: number; y1: number }>> = {
  sword: { x0: -13, x1: 39, y0: -9, y1: 9 },
  spear: { x0: -21, x1: 59, y0: -24, y1: 11 },
  axe: { x0: -14, x1: 38, y0: -20, y1: 11 },
  hammer: { x0: -13, x1: 41, y0: -14, y1: 14 },
  scythe: { x0: -16, x1: 46, y0: -33, y1: 9 },
  bow: { x0: -13, x1: 39, y0: -26, y1: 26 },
  crossbow: { x0: -17, x1: 31, y0: -22, y1: 22 },
  gun: { x0: -20, x1: 46, y0: -11, y1: 9 },
  staff: { x0: -21, x1: 47, y0: -13, y1: 13 },
  claw: { x0: -8, x1: 29, y0: -14, y1: 14 },
};

/**
 * Weapons are drawn along +x from a pivot at the hand.
 *
 * At this scale the weapon is not decoration, it is half the identification:
 * you know a pike block from a bow line by the hedge above it long before you
 * can see a face. So each one gets a real profile — the spear is long and
 * carried at an angle, the bow is drawn with a nocked arrow, the crossbow is a
 * blocky horizontal, the gun has a barrel and a lit match, the hammer is
 * top-heavy, the scythe hooks back on itself — and each gets its own ink
 * outline, because a blade drawn over a body of similar value simply vanishes.
 */
function drawWeapon(g: Graphics, weapon: VisWeapon, p: Palette, sIn: number): void {
  // Steel, not chrome. `p.metal` has already been lifted once for legibility;
  // lifting it again put every hammer head above the value of the man holding
  // it, and a weapon that out-values the soldier is a weapon you read first.
  const metal = shade(p.metal, 1.15);
  const metalLit = shade(p.metal, 1.42);
  const haft = 0x5c4a36;
  const haftDark = 0x3e3122;
  // Authored a size up, then taken back down: the profiles below are easier to
  // reason about at these numbers, and the head weights came out right at 0.88.
  const s = sIn * 0.88;

  switch (weapon) {
    case 'sword': {
      inked(g, (gg) => gg.rect(-8 * s, -1.8 * s, 10 * s, 3.6 * s), haft, 2.8);
      inked(g, (gg) => gg.poly([1.5 * s, -2.2 * s, 29 * s, -2.0 * s, 36 * s, 0, 29 * s, 2.0 * s, 1.5 * s, 2.2 * s]), metal, 3.2);
      g.rect(3 * s, -0.7 * s, 25 * s, 1.4 * s).fill(metalLit);
      inked(g, (gg) => gg.rect(-2.2 * s, -5.6 * s, 3.8 * s, 11.2 * s), p.accent, 2.8);
      g.circle(-8.4 * s, 0, 2.2 * s).fill(shade(p.accent, 0.7));
      break;
    }
    case 'spear': {
      // Carried across the body at a shallow angle rather than levelled: the
      // diagonal is what turns a rank of spearmen into a visible hedge.
      const a = -0.3;
      const cx = Math.cos(a);
      const cy = Math.sin(a);
      const at = (d: number, off: number): [number, number] => [
        (cx * d - cy * off) * s,
        (cy * d + cx * off) * s,
      ];
      const butt = at(-16, 0);
      const grip = at(46, 0);
      inkedStroke(g, (gg) => gg.moveTo(butt[0], butt[1]).lineTo(grip[0], grip[1]), 2.6 * s, haft);
      const t = at(56, 0);
      const l = at(45, -3.4);
      const r = at(45, 3.4);
      const w1 = at(48, -1.4);
      const w2 = at(48, 1.4);
      inked(g, (gg) => gg.poly([t[0], t[1], l[0], l[1], w1[0], w1[1], w2[0], w2[1], r[0], r[1]]), metal, 3.0);
      const cl = at(43, -3.0);
      const cr = at(43, 3.0);
      g.moveTo(cl[0], cl[1]).lineTo(cr[0], cr[1]).stroke({ width: 2.2 * s, color: p.accent });
      const b2 = at(-13, 0);
      g.circle(b2[0], b2[1], 2.0 * s).fill(haftDark);
      break;
    }
    case 'axe': {
      inkedStroke(g, (gg) => gg.moveTo(-9 * s, 0).lineTo(26 * s, 0), 3.0 * s, haft);
      // Bearded, and hanging entirely to one side: an unbalanced outline is the
      // fastest way to say "axe" without drawing an edge.
      inked(
        g,
        (gg) => gg.poly([14 * s, -2.6 * s, 22 * s, -16 * s, 33 * s, -12 * s, 35 * s, -1 * s, 27 * s, 4.6 * s, 15 * s, 3.0 * s]),
        metal,
        3.4,
      );
      g.poly([16 * s, -2.6 * s, 23 * s, -13 * s, 29 * s, -10.6 * s, 24 * s, -1.4 * s]).fill(metalLit);
      break;
    }
    case 'hammer': {
      inkedStroke(g, (gg) => gg.moveTo(-9 * s, 0).lineTo(20 * s, 0), 3.4 * s, haft);
      inked(g, (gg) => gg.rect(18 * s, -9 * s, 11 * s, 18 * s), metal, 3.6);
      g.rect(18 * s, -9 * s, 11 * s, 4.4 * s).fill(metalLit);
      // The beak on the far face, which is what makes it top-heavy and not a box.
      inked(g, (gg) => gg.poly([29 * s, -4.0 * s, 36 * s, 0, 29 * s, 4.0 * s]), shade(metal, 0.85), 2.8);
      g.rect(18 * s, -9 * s, 11 * s, 18 * s).stroke({ width: 1.3, color: p.accent, alpha: 0.7 });
      break;
    }
    case 'scythe': {
      inkedStroke(g, (gg) => gg.moveTo(-11 * s, 3.0 * s).lineTo(28 * s, -1.0 * s), 2.6 * s, haft);
      // The hook curls back over the wielder, which no other weapon here does.
      inkedStroke(
        g,
        (gg) =>
          gg
            .moveTo(27 * s, -1.5 * s)
            .bezierCurveTo(41 * s, -6 * s, 44 * s, -22 * s, 19 * s, -27 * s),
        3.4 * s,
        metal,
      );
      g.moveTo(27 * s, -3.4 * s)
        .bezierCurveTo(38 * s, -8 * s, 40 * s, -20 * s, 20 * s, -24.6 * s)
        .stroke({ width: 1.2 * s, color: metalLit, alpha: 0.9 });
      g.circle(27 * s, -1.5 * s, 2.2 * s).fill(p.accent);
      break;
    }
    case 'bow': {
      // Drawn, not held: limbs bent forward, string hauled back past the hand,
      // an arrow on it. A slack bow reads as a stick.
      inkedStroke(
        g,
        (gg) => gg.moveTo(0, -22 * s).bezierCurveTo(21 * s, -14 * s, 21 * s, 14 * s, 0, 22 * s),
        3.0 * s,
        0x6b5540,
      );
      g.moveTo(0, -22 * s)
        .lineTo(-8 * s, 0)
        .lineTo(0, 22 * s)
        .stroke({ width: 1.3 * s, color: C.boneDim, alpha: 0.9 });
      inkedStroke(g, (gg) => gg.moveTo(-8 * s, 0).lineTo(32 * s, 0), 1.5 * s, C.bone);
      g.poly([36 * s, 0, 29 * s, -2.8 * s, 29 * s, 2.8 * s]).fill(C.boneDim);
      g.rect(9 * s, -4.6 * s, 3.6 * s, 9.2 * s).fill(p.accent);
      break;
    }
    case 'crossbow': {
      // Blocky and horizontal: a stock along the arm and a short thick prod
      // across it. Reads at a glance as "not a bow", which is the entire job.
      inked(g, (gg) => gg.rect(-11 * s, -3.0 * s, 39 * s, 6.0 * s), 0x53422f, 3.2);
      inked(g, (gg) => gg.rect(-13 * s, -4.6 * s, 7 * s, 9.2 * s), haftDark, 2.8);
      inked(
        g,
        (gg) => gg.poly([13 * s, -19 * s, 19 * s, -17 * s, 19 * s, 17 * s, 13 * s, 19 * s, 15 * s, 0]),
        metal,
        3.4,
      );
      g.moveTo(15 * s, -18 * s)
        .lineTo(2 * s, 0)
        .lineTo(15 * s, 18 * s)
        .stroke({ width: 1.3 * s, color: C.boneDim, alpha: 0.85 });
      g.rect(2 * s, -1.3 * s, 24 * s, 2.6 * s).fill(C.bone);
      g.rect(-11 * s, -3.0 * s, 12 * s, 6.0 * s).fill({ color: p.accent, alpha: 0.5 });
      break;
    }
    case 'gun': {
      inked(
        g,
        (gg) => gg.poly([-16 * s, -3.0 * s, -5 * s, -4.6 * s, 3 * s, -3.4 * s, 3 * s, 3.4 * s, -5 * s, 5.0 * s, -16 * s, 4.0 * s]),
        0x3f342a,
        3.2,
      );
      // The barrel is the silhouette: long, thin, and it ends in a bell.
      inked(g, (gg) => gg.rect(2 * s, -3.0 * s, 36 * s, 6.0 * s), metal, 3.2);
      g.rect(2 * s, -3.0 * s, 36 * s, 2.0 * s).fill(metalLit);
      inked(g, (gg) => gg.poly([38 * s, -5.0 * s, 42 * s, -4.0 * s, 42 * s, 4.0 * s, 38 * s, 5.0 * s]), shade(metal, 0.8), 2.8);
      // The lit match, hooked over the lock. It is one dot and it sells the era.
      g.moveTo(-2 * s, -3.6 * s).lineTo(-4 * s, -7.0 * s).stroke({ width: 1.4 * s, color: 0x2e2620 });
      g.circle(-4.4 * s, -7.4 * s, 1.6 * s).fill({ color: C.emberLight, alpha: 0.95 });
      break;
    }
    case 'staff': {
      inkedStroke(g, (gg) => gg.moveTo(-16 * s, 2.4 * s).lineTo(29 * s, -1.2 * s), 2.8 * s, 0x4a3c2c);
      // Topped, and the topping is the point: a cage of horns round a light.
      for (const side of [-1, 1]) {
        inkedStroke(
          g,
          (gg) => gg.moveTo(26 * s, side * 1.5 * s).quadraticCurveTo(38 * s, side * 8 * s, 42 * s, side * 2 * s),
          2.0 * s,
          shade(p.metal, 1.1),
        );
      }
      g.circle(34 * s, 0, 10 * s).fill({ color: p.accent, alpha: 0.2 });
      inked(g, (gg) => gg.circle(34 * s, 0, 5.4 * s), p.accent, 3.0);
      g.circle(33 * s, -1 * s, 2.2 * s).fill(shade(p.accent, 1.5));
      break;
    }
    case 'claw': {
      inked(g, (gg) => gg.ellipse(0, 0, 4.6 * s, 5.6 * s), shade(p.cloth, 0.9), 2.8);
      for (let i = -1; i <= 1; i++) {
        inkedStroke(
          g,
          (gg) =>
            gg
              .moveTo(2 * s, i * 4.4 * s)
              .bezierCurveTo(12 * s, i * 6.4 * s, 19 * s, i * 4.6 * s, 24 * s, i * 1.6 * s),
          2.4 * s,
          C.bone,
        );
      }
      break;
    }
    case 'none':
    default:
      break;
  }
}

// ── Buildings ──────────────────────────────────────────────────────────────

/**
 * The skin a building is drawn in. Eleven peoples' worth of it live in
 * `vernacular.ts`, which is where the argument for the parameter set is written
 * down; this file only knows how to draw them.
 */
type ArchSkin = Vernacular;

/**
 * Marks on a wall that say what it is made of.
 *
 * The cheapest per-race read in the file, and the only one that still works when
 * the wall is thirty pixels wide: four level courses say cut stone, four
 * verticals say sawn board, a scatter of rivets says the Cog-Warren took it off
 * something. Drawn as a handful of marks and never as a pattern — a pattern at
 * this size is noise, which is the lesson the portraits paid for.
 *
 * Cost is irrelevant here: this runs once per building per people, at bake time.
 */
function fabricMarks(g: Graphics, k: ArchSkin, x0: number, top: number, x1: number, bottom: number, seed: number): void {
  const w = x1 - x0;
  const h = bottom - top;
  if (w < 7 || h < 6) return;
  const dark = shade(k.wall, 0.66);
  const light = shade(k.wallLit, 1.12);

  switch (k.fabric) {
    case 'ashlar': {
      const rows = Math.max(2, Math.round(h / 9));
      for (let i = 1; i < rows; i++) {
        const y = top + (h * i) / rows;
        g.moveTo(x0, y).lineTo(x1, y).stroke({ width: 1.2, color: dark, alpha: 0.6 });
        const jx = x0 + w * (i % 2 ? 0.36 : 0.64);
        g.moveTo(jx, y).lineTo(jx, y - h / rows).stroke({ width: 1.2, color: dark, alpha: 0.42 });
      }
      break;
    }
    case 'rubble': {
      for (let i = 0; i < 8; i++) {
        const x = x0 + w * (0.1 + hash2(i, seed, 401) * 0.8);
        const y = top + h * (0.12 + hash2(i, seed, 409) * 0.78);
        g.ellipse(x, y, w * 0.08 + 1.2, h * 0.06 + 1).fill({ color: i % 2 ? dark : light, alpha: 0.35 });
      }
      break;
    }
    case 'log': {
      const rows = Math.max(3, Math.round(h / 6));
      for (let i = 0; i < rows; i++) {
        const y = top + (h * (i + 0.5)) / rows;
        g.moveTo(x0, y).lineTo(x1, y).stroke({ width: 1.5, color: dark, alpha: 0.5 });
        // The end of every third round, showing past the corner post.
        if (i % 3 === 0) g.circle(x0 - 1.4, y, Math.min(2.6, h / rows / 1.7)).fill({ color: k.timber, alpha: 0.9 });
      }
      break;
    }
    case 'plank': {
      const cols = Math.max(3, Math.round(w / 7));
      for (let i = 1; i < cols; i++) {
        const x = x0 + (w * i) / cols;
        g.moveTo(x, top).lineTo(x, bottom).stroke({ width: 1.2, color: i % 3 ? dark : light, alpha: 0.55 });
      }
      break;
    }
    case 'daub': {
      // No coursing at all — the frame showing through the mud, crooked.
      for (let i = 0; i < 3; i++) {
        const x = x0 + w * (0.22 + i * 0.28);
        g.moveTo(x, bottom).lineTo(x + w * 0.05, top).stroke({ width: 2.2, color: k.timber, alpha: 0.75 });
      }
      g.moveTo(x0, top + h * 0.45).lineTo(x1, top + h * 0.38).stroke({ width: 2, color: k.timber, alpha: 0.6 });
      break;
    }
    case 'bone': {
      const cols = Math.max(3, Math.round(w / 8));
      for (let i = 0; i < cols; i++) {
        const x = x0 + (w * (i + 0.5)) / cols;
        g.moveTo(x, bottom).lineTo(x, top + 1).stroke({ width: 2.2, color: light, alpha: 0.5 });
        g.circle(x, top + 1.5, 1.6).fill({ color: light, alpha: 0.55 });
      }
      break;
    }
    case 'hide': {
      // Seams, stitched, running with the slope of the poles.
      for (let i = 1; i < 3; i++) {
        const x = x0 + (w * i) / 3;
        for (let j = 0; j < 5; j++) {
          const y = top + (h * (j + 0.5)) / 5;
          g.moveTo(x - 1.8, y).lineTo(x + 1.8, y - 1).stroke({ width: 1.2, color: dark, alpha: 0.7 });
        }
      }
      break;
    }
    case 'shell': {
      for (let i = 0; i < 4; i++) {
        const x = x0 + w * (0.2 + hash2(i, seed, 421) * 0.6);
        const y = top + h * (0.24 + hash2(i, seed, 431) * 0.6);
        const r = Math.min(w, h) * 0.16;
        for (let j = 1; j <= 2; j++) {
          g.circle(x, y, r * j * 0.7).stroke({ width: 1.2, color: light, alpha: 0.4 });
        }
      }
      break;
    }
    case 'bark': {
      // Grain, and it runs the wrong way round anything cut into it.
      for (let i = 0; i < 4; i++) {
        const x = x0 + w * (0.14 + i * 0.24);
        g.moveTo(x, bottom)
          .quadraticCurveTo(x + w * 0.06, top + h * 0.5, x - w * 0.02, top)
          .stroke({ width: 1.4, color: i % 2 ? dark : light, alpha: 0.42 });
      }
      break;
    }
    case 'plate': {
      const cols = Math.max(2, Math.round(w / 12));
      const rows = Math.max(2, Math.round(h / 12));
      for (let i = 1; i < cols; i++) {
        const x = x0 + (w * i) / cols;
        g.moveTo(x, top).lineTo(x, bottom).stroke({ width: 1.4, color: dark, alpha: 0.65 });
      }
      for (let cx = 0; cx < cols; cx++) {
        for (let cy = 0; cy < rows; cy++) {
          const x = x0 + (w * (cx + 0.5)) / cols;
          const y = top + (h * (cy + 0.5)) / rows;
          // Rivets. Two dots is a repair; a grid of them is a hull.
          g.circle(x - w / cols / 3, y, 1.1).fill({ color: light, alpha: 0.6 });
          g.circle(x + w / cols / 3, y, 1.1).fill({ color: light, alpha: 0.6 });
        }
      }
      break;
    }
    case 'earth': {
      const rows = Math.max(2, Math.round(h / 11));
      for (let i = 1; i < rows; i++) {
        const y = top + (h * i) / rows;
        g.moveTo(x0, y + 1).lineTo(x1, y - 1).stroke({ width: 2.2, color: dark, alpha: 0.3 });
      }
      break;
    }
    default:
      break;
  }
}

/**
 * Every hole cut in every wall, in the shape this people cuts them.
 *
 * `y` is the top of the opening, `w`/`h` its span. Small, and worth doing: a
 * square door and a lancet are two peoples at the size where a door is four
 * pixels, and the alternative — every shape below writing its own `g.rect` —
 * is what made every town in the game look the same from the inside.
 */
function hole(g: Graphics, k: ArchSkin, cx: number, y: number, w: number, h: number, fill = 0x14120f): void {
  const hw = w / 2;
  switch (k.opening) {
    case 'arch':
      g.rect(cx - hw, y + hw, w, Math.max(0, h - hw)).fill(fill);
      g.circle(cx, y + hw, hw).fill(fill);
      break;
    case 'round':
      g.ellipse(cx, y + h / 2, hw, h / 2).fill(fill);
      break;
    case 'slit':
      g.rect(cx - hw * 0.34, y, w * 0.34, h).fill(fill);
      break;
    case 'lancet':
      g.poly([cx - hw * 0.7, y + h, cx - hw * 0.7, y + h * 0.34, cx, y, cx + hw * 0.7, y + h * 0.34, cx + hw * 0.7, y + h]).fill(fill);
      break;
    case 'ragged':
      g.poly([
        cx - hw, y + h,
        cx - hw * 0.82, y + h * 0.2,
        cx - hw * 0.1, y + h * 0.06,
        cx + hw * 0.9, y + h * 0.3,
        cx + hw * 0.72, y + h,
      ]).fill(fill);
      break;
    default:
      g.rect(cx - hw, y, w, h).fill(fill);
      break;
  }
}

/**
 * What is fixed over the door or to the gable end. Always breaks the outline,
 * which is the only reason it earns its place at this size.
 */
function gableOrnament(g: Graphics, k: ArchSkin, cx: number, topY: number, hw: number, rise: number): void {
  const s = Math.max(2.4, hw * 0.3);
  switch (k.ornament) {
    case 'crossed':
      g.moveTo(cx - s, topY - rise * 0.1).lineTo(cx + s * 0.9, topY - rise * 0.62).stroke({ width: 2.6, color: k.timber });
      g.moveTo(cx + s, topY - rise * 0.1).lineTo(cx - s * 0.9, topY - rise * 0.62).stroke({ width: 2.6, color: k.timber });
      break;
    case 'horns':
      for (const d of [-1, 1]) {
        inkedStroke(
          g,
          (gg) =>
            gg
              .moveTo(cx + d * s * 0.3, topY - rise * 0.2)
              .quadraticCurveTo(cx + d * s * 1.5, topY - rise * 0.7, cx + d * s * 0.8, topY - rise * 1.0),
          2.2,
          C.bone,
        );
      }
      g.circle(cx, topY - rise * 0.3, s * 0.32).fill({ color: C.bone, alpha: 0.9 });
      break;
    case 'lamp':
      g.moveTo(cx, topY - rise * 0.16).lineTo(cx + s * 0.9, topY - rise * 0.16).stroke({ width: 1.8, color: k.timber });
      g.circle(cx + s * 0.9, topY - rise * 0.02, s * 0.34).fill({ color: k.trim, alpha: 0.35 });
      g.circle(cx + s * 0.9, topY - rise * 0.02, s * 0.18).fill({ color: 0xffe9b0, alpha: 0.95 });
      break;
    case 'wards':
      for (let i = -1; i <= 1; i++) {
        g.moveTo(cx + i * s * 0.42, topY - rise * 0.06)
          .lineTo(cx + i * s * 0.42, topY - rise * 0.3)
          .stroke({ width: 2, color: k.trim, alpha: 0.85 });
      }
      break;
    case 'chains':
      for (const d of [-1, 1]) {
        g.moveTo(cx + d * hw, topY)
          .quadraticCurveTo(cx + d * hw * 0.5, topY + rise * 0.28, cx, topY + rise * 0.04)
          .stroke({ width: 1.8, color: k.trim, alpha: 0.8 });
      }
      break;
    case 'totem': {
      const px = cx - hw * 1.02;
      inkedStroke(g, (gg) => gg.moveTo(px, topY + rise * 0.9).lineTo(px, topY - rise * 0.8), 3.4, k.timber);
      for (let i = 0; i < 3; i++) {
        g.circle(px, topY - rise * (0.5 - i * 0.34), 2.2).fill({ color: k.trim, alpha: 0.9 });
      }
      break;
    }
    case 'scrap':
      g.poly([cx - s, topY - rise * 0.1, cx + s * 0.6, topY - rise * 0.46, cx + s * 0.9, topY - rise * 0.2, cx - s * 0.6, topY + rise * 0.06])
        .fill({ color: k.trim, alpha: 0.75 });
      break;
    case 'pipe':
      inkedStroke(g, (gg) => gg.moveTo(cx + hw * 0.5, topY).lineTo(cx + hw * 0.5, topY - rise * 0.9), 3.0, shade(k.wall, 0.8));
      g.ellipse(cx + hw * 0.5, topY - rise * 0.92, 3.4, 1.8).fill(shade(k.wallLit, 1.1));
      g.moveTo(cx + hw * 0.5, topY - rise * 0.5).lineTo(cx - hw * 0.6, topY - rise * 0.06).stroke({ width: 1.2, color: k.trim, alpha: 0.7 });
      break;
    case 'ribs':
      for (let i = 0; i < 2; i++) {
        inkedStroke(
          g,
          (gg) =>
            gg
              .moveTo(cx - hw * (0.6 + i * 0.2), topY)
              .quadraticCurveTo(cx, topY - rise * (0.3 + i * 0.22), cx + hw * (0.6 + i * 0.2), topY),
          2.2,
          k.trim,
          0.85,
        );
      }
      break;
    case 'blooms':
      for (let i = -1; i <= 1; i++) {
        const ex = cx + i * hw * 0.6;
        inkedStroke(
          g,
          (gg) => gg.moveTo(ex, topY).quadraticCurveTo(ex + i * s * 0.5, topY - rise * 0.4, ex + i * s * 0.2, topY - rise * 0.72),
          1.8,
          k.trim,
          0.9,
        );
        g.circle(ex + i * s * 0.2, topY - rise * 0.76, 2.2).fill({ color: k.trim, alpha: 0.8 });
      }
      break;
    default:
      break;
  }
}

/**
 * The crown: whatever the building's raised mass is capped with.
 *
 * `x0..x1` is the horizontal span of the mass, `topY` where the wall stops and
 * `rise` how far above it the crown reaches. Everything here gets an ink
 * outline for the same reason units do — a roof the colour of ash, against ash,
 * is not a roof.
 */
/**
 * How far above the wall head a crown actually reaches, as a multiple of the
 * rise it was given.
 *
 * Needed because the crowns are not all the same height and the motifs have to
 * sit *on* them. `battlement` occupies a little over half its rise; a `spire`
 * puts a needle well above it. Anchoring every motif to the nominal rise instead
 * left the counting house's scales floating a clear building's width above its
 * own merlons — visible instantly in the contact sheet and invisible in the
 * source, which is the whole argument for having a contact sheet.
 */
function crownReach(k: ArchSkin): number {
  switch (k.crown) {
    case 'battlement':
      return 0.62;
    case 'flat':
      return 0.58;
    case 'deck':
      return 0.66;
    case 'gable':
    case 'cone':
      return 0.45 + 0.55 * k.pitch;
    case 'spire':
      return 1.24;
    case 'cap':
      return 1.14;
    case 'pagoda':
      return 0.95;
    default:
      return 1;
  }
}

function drawCrown(g: Graphics, k: ArchSkin, x0: number, x1: number, topY: number, rise: number): void {
  const cx = (x0 + x1) / 2;
  const hw = Math.max(4, (x1 - x0) / 2);
  // Pitch is a property of the people, not of the building: the same hall is a
  // slab in Karrun-Dûr and a knife in the Crimson Assize. Only the crowns that
  // are actually *roofs* answer to it — a battlement does not get steeper.
  const pitched = k.crown === 'gable' || k.crown === 'cone' || k.crown === 'spire' || k.crown === 'pagoda';
  // Clamped against the span for the same reason `roofed` is: a crown taller
  // than the mass is wide stops being a roof and starts being the building.
  const apex = pitched
    ? Math.max(topY - hw * 2.1, topY - rise * (0.45 + 0.55 * k.pitch))
    : topY - rise;

  switch (k.crown) {
    case 'battlement': {
      // A row of teeth. The skyline of a stone town is square and it repeats.
      const y = topY - rise * 0.4;
      inked(g, (gg) => gg.rect(x0, y, hw * 2, topY - y + 2), k.roof, 2.6);
      g.rect(x0, y, hw * 2, 3).fill(k.wallLit);
      const n = Math.max(3, Math.round(hw / 7));
      const w = (hw * 2) / (n * 2 - 1);
      for (let i = 0; i < n; i++) {
        inked(g, (gg) => gg.rect(x0 + i * w * 2, y - w * 1.0, w, w * 1.2), k.wallLit, 2.0);
      }
      break;
    }
    case 'gable': {
      inked(g, (gg) => gg.poly([x0 - hw * 0.16, topY + 2, cx, apex, x1 + hw * 0.16, topY + 2]), k.roof, 3.0);
      g.poly([x0 - hw * 0.16, topY + 2, cx, apex, cx, topY + 2]).fill(k.roofLit);
      // Crossed barge-boards over the ridge — the thing that says "timber".
      g.moveTo(cx - hw * 0.3, apex + hw * 0.34)
        .lineTo(cx + hw * 0.26, apex - hw * 0.2)
        .stroke({ width: 2.6, color: k.timber });
      g.moveTo(cx + hw * 0.3, apex + hw * 0.34)
        .lineTo(cx - hw * 0.26, apex - hw * 0.2)
        .stroke({ width: 2.6, color: k.timber });
      break;
    }
    case 'dome': {
      inked(g, (gg) => gg.ellipse(cx, topY, hw, rise), k.roof, 3.0);
      g.ellipse(cx - hw * 0.24, topY, hw * 0.62, rise * 0.72).fill(k.roofLit);
      inked(g, (gg) => gg.circle(cx, apex + 2, hw * 0.14), k.trim, 2.2);
      break;
    }
    case 'spire': {
      inked(g, (gg) => gg.ellipse(cx, topY, hw * 0.9, rise * 0.55), k.roof, 3.0);
      g.ellipse(cx - hw * 0.22, topY, hw * 0.54, rise * 0.4).fill(k.roofLit);
      // A needle above the dome, cross-barred. Gilded towns have a skyline of them.
      inked(g, (gg) => gg.poly([cx - 2.4, topY - rise * 0.5, cx, apex - rise * 0.24, cx + 2.4, topY - rise * 0.5]), k.trim, 2.4);
      g.rect(cx - hw * 0.24, apex + rise * 0.14, hw * 0.48, 2.6).fill(k.trim);
      break;
    }
    case 'cone': {
      inked(g, (gg) => gg.poly([x0 + hw * 0.1, topY + 2, cx, apex - rise * 0.2, x1 - hw * 0.1, topY + 2]), k.roof, 3.0);
      g.poly([x0 + hw * 0.1, topY + 2, cx, apex - rise * 0.2, cx, topY + 2]).fill(k.roofLit);
      // Lodge poles crossing above the smoke hole.
      for (const d of [-1, 1]) {
        g.moveTo(cx + d * hw * 0.12, apex + rise * 0.1)
          .lineTo(cx - d * hw * 0.36, apex - rise * 0.42)
          .stroke({ width: 2.2, color: k.timber });
      }
      break;
    }
    case 'cap': {
      // A mushroom cap, overhanging the walls on every side, gilled beneath.
      inked(g, (gg) => gg.ellipse(cx, topY - rise * 0.42, hw * 1.32, rise * 0.72), k.roof, 3.2);
      g.ellipse(cx - hw * 0.3, topY - rise * 0.52, hw * 0.72, rise * 0.42).fill(k.roofLit);
      for (let i = -2; i <= 2; i++) {
        g.moveTo(cx + i * hw * 0.42, topY - rise * 0.18)
          .lineTo(cx + i * hw * 0.5, topY + 2)
          .stroke({ width: 1.8, color: k.trim, alpha: 0.55 });
      }
      break;
    }
    case 'rib': {
      // A ribcage over the roof. Three arches and a pair of horns.
      for (let i = 0; i < 3; i++) {
        const t = 0.42 + i * 0.26;
        inkedStroke(
          g,
          (gg) =>
            gg
              .moveTo(x0 + hw * 0.16, topY + 2)
              .quadraticCurveTo(cx, apex - rise * (t - 0.3), x1 - hw * 0.16, topY + 2),
          2.8,
          i === 1 ? k.trim : k.roofLit,
        );
      }
      for (const d of [-1, 1]) {
        inked(g, (gg) => gg.poly([cx + d * hw * 0.5, topY, cx + d * hw * 1.15, apex + rise * 0.1, cx + d * hw * 0.72, topY - rise * 0.1]), k.trim, 2.4);
      }
      break;
    }
    case 'lean': {
      // Nobody in the Horde has ever finished a roof. One slope, too steep, and
      // a handful of stakes driven through it at the high end.
      inked(g, (gg) => gg.poly([x0 - hw * 0.1, topY + 2, x0 + hw * 0.2, apex, x1 + hw * 0.12, topY - rise * 0.24, x1 + hw * 0.1, topY + 2]), k.roof, 3.0);
      g.poly([x0 - hw * 0.1, topY + 2, x0 + hw * 0.2, apex, cx, topY - rise * 0.1, cx, topY + 2]).fill(k.roofLit);
      for (let i = 0; i < 3; i++) {
        const x = x0 + hw * (0.3 + i * 0.5);
        g.moveTo(x, topY - rise * (0.5 - i * 0.1))
          .lineTo(x + 3.4, apex - rise * (0.18 + i * 0.06))
          .stroke({ width: 2.2, color: k.timber });
      }
      break;
    }
    /**
     * A deck, a rail and a stub stack. The Cog-Warren does not roof a building,
     * it puts a floor on top of it and stands on that.
     *
     * This replaced `mound`, which was a heap of spoil with a hole in the middle
     * — right for a burrow entrance and catastrophic on a hall, where it read as
     * a black doughnut sitting on every roof in the settlement. Worth recording
     * because it is the general failure of this pass: a crown authored against
     * one shape and applied to twenty-five.
     */
    case 'deck': {
      inked(g, (gg) => gg.rect(x0 - hw * 0.14, topY - rise * 0.26, x1 - x0 + hw * 0.28, rise * 0.3), k.roof, 2.8);
      g.rect(x0 - hw * 0.14, topY - rise * 0.26, x1 - x0 + hw * 0.28, 3).fill(k.roofLit);
      // A rail, in two runs with a gap where the ladder comes up.
      const railY = topY - rise * 0.58;
      for (const seg of [[0.02, 0.42], [0.6, 0.98]]) {
        g.moveTo(x0 + (x1 - x0) * seg[0], railY)
          .lineTo(x0 + (x1 - x0) * seg[1], railY)
          .stroke({ width: 2, color: k.trim, alpha: 0.85 });
      }
      for (const t of [0.02, 0.42, 0.6, 0.98]) {
        const x = x0 + (x1 - x0) * t;
        g.moveTo(x, railY).lineTo(x, topY - rise * 0.24).stroke({ width: 1.8, color: k.trim, alpha: 0.8 });
      }
      break;
    }
    /**
     * A slab, and a parapet round it. Karrun-Dûr does not build roofs: a roof is
     * a thing that keeps snow off a chimney, and everything under it is stone
     * anyway. Read against a town of gables it is unmistakable, which is the
     * whole point of the parameter.
     */
    case 'flat': {
      inked(g, (gg) => gg.rect(x0 - hw * 0.08, topY - rise * 0.3, (x1 - x0) + hw * 0.16, rise * 0.34), k.roof, 2.8);
      g.rect(x0 - hw * 0.08, topY - rise * 0.3, (x1 - x0) + hw * 0.16, 3).fill(k.roofLit);
      // A low kerb at each end, so the slab has a thickness.
      for (const d of [-1, 1]) {
        inked(g, (gg) => gg.rect(cx + d * hw - (d > 0 ? 4 : 0), topY - rise * 0.52, 4, rise * 0.24), k.wallLit, 2.0);
      }
      break;
    }
    /**
     * Tiers. The Silver Reaches build in stages and never in a lump, so their
     * skyline is a set of stacked skirts rather than one triangle — narrow,
     * repeated, and legible at any zoom the building is.
     */
    case 'pagoda': {
      for (let i = 0; i < 3; i++) {
        const t = i / 3;
        const w = hw * (1.24 - t * 0.62);
        const y = topY - (apex - topY) * -t * 0 - rise * t * 0.62;
        inked(g, (gg) => gg.poly([cx - w, y + 2, cx, y - rise * 0.3, cx + w, y + 2]), i === 2 ? k.roofLit : k.roof, 2.6);
        g.moveTo(cx - w, y + 2).lineTo(cx + w, y + 2).stroke({ width: 1.6, color: k.trim, alpha: 0.5 });
      }
      inkedStroke(g, (gg) => gg.moveTo(cx, apex - rise * 0.1).lineTo(cx, apex - rise * 0.42), 2.2, k.trim);
      break;
    }
    case 'branch': {
      // Grown, not built. A lumpy mass with spurs going up out of it.
      inked(g, (gg) => gg.ellipse(cx, topY - rise * 0.28, hw * 1.05, rise * 0.62), k.roof, 3.2);
      g.ellipse(cx - hw * 0.3, topY - rise * 0.36, hw * 0.56, rise * 0.36).fill(k.roofLit);
      for (let i = 0; i < 4; i++) {
        const d = -1 + (i / 3) * 2;
        inkedStroke(
          g,
          (gg) =>
            gg
              .moveTo(cx + d * hw * 0.6, topY - rise * 0.3)
              .quadraticCurveTo(cx + d * hw * 1.0, apex + rise * 0.2, cx + d * hw * 0.78, apex - rise * 0.12),
          2.4,
          k.trim,
          0.85,
        );
      }
      break;
    }
    default:
      break;
  }
}

/**
 * Ploughed ground: parallel ridges inside a fenced rectangle.
 *
 * Shared by the farm and the caltrop field, which is the point — the furrows
 * say "this is worked ground and nothing stands on it", and the farm's barn on
 * the corner is the only thing that separates the two.
 */
function furrows(g: Graphics, k: ArchSkin, W: number, H: number): void {
  g.rect(2, 2, W - 4, H - 4).fill(0x35301f);
  const rows = Math.max(3, Math.round(H / 10));
  for (let i = 0; i < rows; i++) {
    const y = 4 + (i * (H - 8)) / rows;
    g.rect(4, y, W - 8, 2.6).fill({ color: 0x4a4426, alpha: 0.85 });
    g.rect(4, y + 2.6, W - 8, 1.4).fill({ color: 0x241f16, alpha: 0.7 });
  }
  g.rect(2, 2, W - 4, H - 4).stroke({ width: 1.6, color: k.timber });
}

/** A few style props scattered round the footprint. Cheap, and it doubles the read. */
function drawLitter(g: Graphics, k: ArchSkin, W: number, H: number, seed: number): void {
  if (k.litter === 'none') return;
  for (let i = 0; i < 5; i++) {
    const x = 2 + hash2(i, seed, 131) * (W - 4);
    const y = H * 0.62 + hash2(i, seed, 137) * H * 0.34;
    switch (k.litter) {
      case 'stakes':
        g.moveTo(x, y).lineTo(x + 2.2, y - 9).stroke({ width: 2.2, color: k.timber });
        break;
      case 'roots':
        g.moveTo(x - 5, y).quadraticCurveTo(x, y - 4, x + 6, y).stroke({ width: 2.0, color: k.trim, alpha: 0.7 });
        break;
      case 'bones':
        g.moveTo(x - 4, y).lineTo(x + 4, y - 2).stroke({ width: 2.0, color: k.trim, alpha: 0.8 });
        g.circle(x - 4.4, y, 1.6).fill({ color: k.trim, alpha: 0.8 });
        break;
      case 'poles':
        g.moveTo(x, y).lineTo(x - 2.4, y - 12).stroke({ width: 2.0, color: k.timber });
        break;
      case 'spoil':
        g.ellipse(x, y, 5.4, 2.6).fill({ color: k.roofLit, alpha: 0.8 });
        break;
      case 'caps':
        g.ellipse(x, y - 3, 4.2, 2.4).fill({ color: k.trim, alpha: 0.7 });
        g.rect(x - 0.9, y - 3, 1.8, 3.4).fill({ color: k.wallLit, alpha: 0.8 });
        break;
      case 'polyps':
        g.circle(x, y, 2.6).fill({ color: k.trim, alpha: 0.55 });
        g.circle(x, y, 1.2).fill({ color: 0x0e1418, alpha: 0.7 });
        break;
      case 'rubble':
        g.poly([x - 3, y + 1.6, x - 1, y - 2.6, x + 3, y + 1]).fill({ color: k.wallLit, alpha: 0.7 });
        break;
      case 'scrap':
        // Plate, bent, leaning where it was dropped.
        g.poly([x - 4, y + 1, x - 2.4, y - 5, x + 2.6, y - 3.4, x + 3.4, y + 1.4]).fill({ color: k.wallLit, alpha: 0.75 });
        g.circle(x - 1, y - 2, 1).fill({ color: k.trim, alpha: 0.8 });
        break;
      case 'lamps':
        g.moveTo(x, y).lineTo(x - 1.4, y - 10).stroke({ width: 1.8, color: k.timber });
        g.circle(x - 1.6, y - 10.6, 2.6).fill({ color: k.trim, alpha: 0.28 });
        g.circle(x - 1.6, y - 10.6, 1.3).fill({ color: 0xffe9b0, alpha: 0.9 });
        break;
      case 'chains':
        g.moveTo(x - 5, y - 8).quadraticCurveTo(x, y - 2, x + 5, y - 8).stroke({ width: 1.6, color: k.trim, alpha: 0.7 });
        break;
      default:
        break;
    }
  }
}

/**
 * Buildings are drawn as a footprint plus a raised mass above it, so the
 * settlement reads as a town rather than a set of floor tiles. The base sits at
 * the bottom of the texture; `pad` is the headroom above the footprint.
 *
 * The *shape* is what the building does — a forge has a chimney wherever it is
 * built. The *skin* is who built it, and it supplies the colours and, more to
 * the point, the crown: the same hall is a crenellated block in Karrun-Dûr, a
 * steep gable in an Ashen village, a fungal cap in a Rotmoot camp.
 */
function drawBuilding(g: Graphics, def: BuildingType, k: ArchSkin, W: number, H: number, pad: number): void {
  const vis = def.vis;
  const seed = def.w * 31 + def.h * 7 + vis.detail;

  /**
   * The three numbers the envelope comes down to.
   *
   * `stance` makes a mass taller and, in the same move, narrower — because a
   * building that only grows upward reads as the same building stretched, and
   * one that narrows as it grows reads as a different country. `sink` takes the
   * top of the mass *down toward its own floor* rather than moving the
   * footprint, so a dug-in people keeps the footprint the rules gave it and
   * loses the elevation instead. `squeeze` is the horizontal half of `stance`.
   */
  /**
   * Five shapes are exempt from the envelope, and the exemption is the whole
   * reason the two axes can coexist.
   *
   * A tower is the one silhouette allowed to be thin, and *that* is what says
   * "tower" before anything else registers. Let a squat people's stance crush it
   * and a Karrun-Dûr watchtower becomes a shed with a ladder — which is exactly
   * what the first render of this showed, along with a belfry too short to hang
   * a bell in. The same goes for a shrine, which is narrow-and-reaching by
   * definition, and a mill, whose sails have to clear its own roof.
   *
   * So these keep a damped stance and take no sink at all. A dwarf tower is
   * still visibly dwarvish — battered ashlar, a slab cap, warded lintel — and it
   * is still unmistakably a tower. Function wins where the two axes collide,
   * because a player who cannot find the granary has lost more than a player who
   * cannot tell two peoples apart.
   */
  const tall =
    vis.shape === 'tower' ||
    vis.shape === 'belfry' ||
    vis.shape === 'shrine' ||
    vis.shape === 'windmill' ||
    vis.shape === 'hearth';
  const sink = tall ? 0 : k.sunken;
  const rise = pad * 0.92 * (tall ? 0.8 + 0.2 * k.stance : k.stance * (1 - sink * 0.42));
  const squeeze = 1 / (0.55 + 0.45 * k.stance);

  // Ground shadow across the footprint.
  g.ellipse(W / 2, H - H * 0.12, W * 0.55, H * 0.34).fill({ color: 0x0a0908, alpha: 0.4 });
  g.rect(1, 1, W - 2, H - 2).fill({ color: 0x1d1a16, alpha: 0.5 });
  if (sink > 0.05) {
    // A bank of spoil round anything that was dug rather than raised. Without
    // it a sunken mass just looks like a short one.
    g.ellipse(W / 2, H * 0.72, W * 0.56, H * 0.3).fill({ color: shade(k.wall, 0.7), alpha: 0.7 * sink + 0.3 });
    g.ellipse(W / 2, H * 0.7, W * 0.4, H * 0.2).fill({ color: shade(k.wall, 0.5), alpha: 0.5 });
  }
  drawLitter(g, k, W, H, seed);

  /**
   * Where the last raised mass ended up. A motif has to hang off *something*,
   * and every shape below puts its mass somewhere different, so the two
   * builders record it rather than the motif guessing.
   */
  let box = { x0: W * 0.2, x1: W * 0.8, top: H * 0.4, bottom: H * 0.9, peak: H * 0.4 - rise * 0.5 };

  /** Apply the people's stance and sink to a mass a shape asked for. */
  const shapeMass = (x0: number, x1: number, top: number, bottom: number) => {
    const cx = (x0 + x1) / 2;
    return {
      x0: cx + (x0 - cx) * squeeze,
      x1: cx + (x1 - cx) * squeeze,
      top: top + (bottom - top) * sink * 0.42,
      bottom,
      peak: top,
    };
  };

  /**
   * The wall itself: battered or plumb, and marked with whatever this people
   * builds out of. Batter is drawn rather than faked — the outline of a
   * Karrun-Dûr wall genuinely leans in, and at four tiles that lean is most of
   * what says "dwarves" before a single merlon registers.
   */
  const walls = (x0: number, x1: number, top: number, bottom: number) => {
    const lean = (bottom - top) * k.batter * 0.5;
    inked(g, (gg) => gg.poly([x0 + lean, top, x1 - lean, top, x1, bottom, x0, bottom]), k.wall, 3.0);
    g.poly([x0 + lean, top, x0 + lean + (x1 - x0) * 0.34, top, x0 + (x1 - x0) * 0.34, bottom, x0, bottom]).fill(k.wallLit);
    g.rect(x0 + lean, top, x1 - x0 - lean * 2, 3.4).fill(k.wallLit);
    fabricMarks(g, k, x0 + lean, top + 4, x1 - lean, bottom, seed);
  };

  /** The standard walled mass: a block from `top` down to `bottom`, then a crown. */
  const mass = (x0In: number, x1In: number, topIn: number, bottom: number, up: number) => {
    const m = shapeMass(x0In, x1In, topIn, bottom);
    /**
     * Stance is damped where it lifts, and not where it narrows.
     *
     * The first cut multiplied the crown height by `stance` outright, on top of
     * a pitch that was also multiplied by it, and the result was a Silver
     * Reaches town in which every building was a hat with a sliver of wall under
     * it — the second acceptance test passing at the cost of the first. A tall
     * people is *narrow* first and *high* second.
     */
    const lift = up * (tall ? 1 : 0.62 + 0.38 * k.stance);
    m.peak = m.top - lift * crownReach(k);
    box = m;
    walls(m.x0, m.x1, m.top, m.bottom);
    drawCrown(g, k, m.x0, m.x1, m.top, lift);
    gableOrnament(g, k, (m.x0 + m.x1) / 2, m.top, (m.x1 - m.x0) / 2, lift);
  };

  /**
   * The same mass, roofed rather than crowned.
   *
   * The faction crown is a *statement* — a slab in Karrun-Dûr, a fungal cap in a
   * Rotmoot camp — and it belongs on the buildings that make one: the hall, the
   * shrine, the tower, the forge. Put it on everything and a hovel, a woodshed
   * and a stable all become small castles, which is exactly what the first pass
   * of this looked like. Dwellings and outbuildings get a plain pitched roof —
   * but the *pitch* and the *overhang* are still the people's, because a roof
   * that keeps the rain off is not an opinion about who built it and the angle
   * of it very much is.
   */
  const roofed = (x0In: number, x1In: number, topIn: number, bottom: number, up: number) => {
    const m = shapeMass(x0In, x1In, topIn, bottom);
    box = m;
    walls(m.x0, m.x1, m.top, m.bottom);
    const { x0, x1, top } = m;
    const cx = (x0 + x1) / 2;
    const over = (x1 - x0) * (0.09 + k.eave);
    /**
     * A roof may not be taller than the mass is wide.
     *
     * Without the clamp a steep people's storehouse is a triangle with no wall
     * under it, and a storehouse is *long and low* — that is the whole of how
     * you know it from a hall. The pitch still spreads the towns apart across
     * more than a factor of four; it simply stops eating the building.
     */
    const peak = Math.max(
      top - (x1 - x0) * 0.95,
      top - up * (0.45 + 0.55 * k.pitch) * (tall ? 1 : 0.62 + 0.38 * k.stance),
    );
    m.peak = peak;
    inked(g, (gg) => gg.poly([x0 - over, top + 2, cx, peak, x1 + over, top + 2]), k.roof, 3.0);
    g.poly([x0 - over, top + 2, cx, peak, cx, top + 2]).fill(k.roofLit);
    // Two courses of thatch or shingle across the slope, so it is not a flat
    // triangle at any zoom that can see it at all.
    for (const t of [0.42, 0.72]) {
      g.moveTo(cx - (cx - x0) * t, top + 2 - (top - peak) * (1 - t))
        .lineTo(cx + (cx - x0) * t, top + 2 - (top - peak) * (1 - t))
        .stroke({ width: 1.6, color: k.roofLit, alpha: 0.5 });
    }
    // The eave line, drawn dark: an overhang that casts no shadow is a hat that
    // is not on the head.
    if (k.eave > 0.14) {
      g.moveTo(x0 - over, top + 3).lineTo(x1 + over, top + 3).stroke({ width: 2, color: 0x14120f, alpha: 0.5 });
    }
    gableOrnament(g, k, cx, top, (x1 - x0) / 2, up * k.stance);
    box = m;
  };

  /** A fence of posts and rails round a patch of ground. */
  const fence = (x0: number, y0: number, x1: number, y1: number) => {
    g.rect(x0, y0, x1 - x0, y1 - y0).stroke({ width: 2.4, color: k.timber });
    const n = Math.max(3, Math.round((x1 - x0) / 11));
    for (let i = 0; i <= n; i++) {
      const x = x0 + ((x1 - x0) * i) / n;
      g.rect(x - 1.3, y0 - 3, 2.6, 5).fill(k.timber);
      g.rect(x - 1.3, y1 - 2, 2.6, 5).fill(k.timber);
    }
  };

  /** A stack with a live coal at the top of it. What makes a forge a forge. */
  const chimney = (x: number, wide: number, top: number, bottom: number, glow: number) => {
    inked(g, (gg) => gg.rect(x, top, wide, bottom - top), shade(k.wall, 0.86), 2.8);
    g.rect(x, top, wide * 0.4, bottom - top).fill(shade(k.wallLit, 0.9));
    g.circle(x + wide / 2, top, wide * 0.62).fill({ color: C.ember, alpha: 0.35 + glow * 0.3 });
    g.circle(x + wide / 2, top, wide * 0.3).fill({ color: C.emberLight, alpha: 0.5 + glow * 0.4 });
  };

  switch (vis.shape) {
    case 'hearth': {
      g.rect(W * 0.1, H * 0.14, W * 0.8, H * 0.78).fill(shade(k.wall, 0.82));
      g.rect(W * 0.1, H * 0.14, W * 0.8, 5).fill(k.wallLit);
      // A broken tower with the fire at its heart.
      mass(W * 0.26, W * 0.74, -rise * 0.55, H * 0.5, rise * 0.6);
      for (let i = 0; i < 3; i++) {
        g.circle(W * 0.5, -rise * 0.2, (18 - i * 5) * (W / 96)).fill({
          color: [C.emberLight, C.ember, C.gold][i],
          alpha: 0.55 - i * 0.1,
        });
      }
      g.circle(W * 0.5, -rise * 0.2, 5 * (W / 96)).fill(0xffe9b0);
      break;
    }
    /**
     * A tower is the one silhouette allowed to be *thin*.
     *
     * That is the whole read: everything else in a settlement is about as wide
     * as it is tall, so a shaft a quarter of its own height across is a tower
     * before anything else about it has registered. The three details are the
     * three jobs — a lookout is a timber frame, a shooting tower is
     * crenellated stone, an engine tower carries a machine — and a glow puts a
     * light on top of any of them.
     */
    case 'tower': {
      const r = Math.min(W, H) * 0.26;
      const cx = W / 2;
      const topY = H * 0.62 - rise;
      g.ellipse(cx, H * 0.68, r * 1.3, r * 0.62).fill(shade(k.wall, 0.6));
      if (vis.detail <= 1) {
        // A lookout: four legs and a platform, not masonry.
        for (const d of [-1, 1]) {
          inkedStroke(
            g,
            (gg) => gg.moveTo(cx + d * r * 1.2, H * 0.76).lineTo(cx + d * r * 0.5, topY + r),
            3.4,
            k.timber,
          );
        }
        for (let i = 1; i <= 2; i++) {
          const y = topY + r + ((H * 0.76 - topY - r) * i) / 3;
          const sp = r * (0.5 + (i / 3) * 0.7);
          g.moveTo(cx - sp, y).lineTo(cx + sp, y).stroke({ width: 2.2, color: k.timber });
        }
        inked(g, (gg) => gg.rect(cx - r * 1.2, topY, r * 2.4, r * 1.1), k.roof, 2.8);
        g.rect(cx - r * 1.2, topY, r * 2.4, 3).fill(k.roofLit);
      } else {
        inked(g, (gg) => gg.rect(cx - r, topY, r * 2, H * 0.68 - topY), k.wall, 3.0);
        g.rect(cx - r, topY, r * 0.7, H * 0.68 - topY).fill(k.wallLit);
        // Arrow slits, all the way up. Nothing else in the town has them.
        for (let i = 0; i < 3; i++) {
          const y = topY + r * 1.2 + i * ((H * 0.6 - topY) / 3.4);
          g.rect(cx - 1.7, y, 3.4, r * 0.7).fill(0x100e0c);
        }
        // The head overhangs, so the outline steps out at the top.
        inked(g, (gg) => gg.rect(cx - r * 1.36, topY - r * 0.55, r * 2.72, r * 0.66), k.wall, 2.6);
        const teeth = 4;
        const tw = (r * 2.72) / (teeth * 2 - 1);
        for (let i = 0; i < teeth; i++) {
          inked(g, (gg) => gg.rect(cx - r * 1.36 + i * tw * 2, topY - r * 1.16, tw, r * 0.64), k.wallLit, 2.0);
        }
        if (vis.detail >= 3 && vis.glow <= 0) {
          // An engine on the roof: a beam across a pivot, and a bolt on it.
          inkedStroke(
            g,
            (gg) => gg.moveTo(cx - r * 1.15, topY - r * 2.0).lineTo(cx + r * 1.15, topY - r * 1.55),
            3.2,
            k.timber,
          );
          inkedStroke(g, (gg) => gg.moveTo(cx, topY - r * 2.5).lineTo(cx, topY - r * 1.25), 2.6, k.trim);
        }
      }
      if (vis.glow > 0) {
        g.circle(cx, topY - r * 1.6, r * 0.95).fill({ color: k.trim, alpha: 0.3 });
        g.circle(cx, topY - r * 1.6, r * 0.52).fill({ color: 0xdff0ff, alpha: 0.55 + vis.glow * 0.35 });
      }
      break;
    }

    /** Open arches with a bell in them. Nothing else is hollow at the top. */
    case 'belfry': {
      const hw = Math.min(W, H) * 0.3;
      const cx = W / 2;
      const topY = H * 0.6 - rise;
      inked(g, (gg) => gg.rect(cx - hw, topY, hw * 2, H * 0.66 - topY), k.wall, 3.0);
      g.rect(cx - hw, topY, hw * 0.6, H * 0.66 - topY).fill(k.wallLit);
      // The belfry stage: a black opening with a round head.
      const oy = topY + hw * 0.5;
      g.rect(cx - hw * 0.6, oy, hw * 1.2, hw * 1.5).fill(0x0c0a09);
      g.circle(cx, oy, hw * 0.6).fill(0x0c0a09);
      // The bell.
      inked(
        g,
        (gg) =>
          gg.poly([
            cx - hw * 0.38, oy + hw * 1.1,
            cx - hw * 0.26, oy + hw * 0.32,
            cx + hw * 0.26, oy + hw * 0.32,
            cx + hw * 0.38, oy + hw * 1.1,
          ]),
        k.trim,
        2.2,
      );
      g.rect(cx - hw * 0.44, oy + hw * 1.1, hw * 0.88, 2.6).fill(k.trim);
      drawCrown(g, k, cx - hw, cx + hw, topY, rise * 0.35);
      break;
    }

    case 'hall': {
      mass(W * 0.06, W * 0.94, H * 0.28, H * 0.94, rise * 0.85);
      // Timber frame.
      for (let i = 1; i < 4; i++) {
        g.rect(W * (0.06 + i * 0.22), H * 0.46, 3, H * 0.48).fill({ color: k.timber, alpha: 0.8 });
      }
      g.rect(W * 0.44, H * 0.66, W * 0.14, H * 0.28).fill(0x14120f);
      break;
    }

    /** Stall doors in a row, and a rail out front. Count the doors. */
    case 'stable': {
      roofed(W * 0.06, W * 0.94, H * 0.34, H * 0.9, rise * 0.68);
      const stalls = Math.max(2, Math.round(W / 30));
      const sw = (W * 0.78) / stalls;
      for (let i = 0; i < stalls; i++) {
        const x = W * 0.11 + i * sw;
        g.rect(x, H * 0.56, sw - 5, H * 0.34).fill(0x14120f);
        g.rect(x, H * 0.56, sw - 5, H * 0.12).fill({ color: k.timber, alpha: 0.8 });
      }
      g.moveTo(W * 0.06, H * 0.96).lineTo(W * 0.94, H * 0.96).stroke({ width: 2.4, color: k.timber });
      break;
    }

    /**
     * A dwelling, and it has to read as one before it reads as anything else.
     *
     * This is the acceptance test the owner actually named — *a house must read
     * as a house* — and it is won by making every dwelling in the game one
     * family and making nothing else in the settlement join it. Three marks do
     * the whole job and none of them is a colour:
     *
     *   the eaves come down low      a house is mostly roof. Halls, stores and
     *                                forges all show more wall than roof, so a
     *                                squat mass under a big one is a *home*
     *   the door is small, and on    everything else in the town has a cart
     *   the ground, with a step      door, a hole in the earth, or no door
     *   one window, and it is lit    the cheapest "somebody lives here" there
     *                                is, and it survives to two tiles
     *
     * The chimney is not drawn here. It is the motif, so a house has one and a
     * longhouse has three, and both are legible against a skyline of towers.
     */
    case 'hut': {
      const bays = vis.detail > 1 ? Math.max(2, Math.round(W / 46)) : 1;
      // Deliberately low: the wall top sits below half height, so the roof takes
      // more of the silhouette than the wall does.
      roofed(W * 0.11, W * 0.89, H * 0.52, H * 0.92, rise * 0.95);
      for (let i = 0; i < bays; i++) {
        const t = bays === 1 ? 0.5 : 0.24 + (i * 0.52) / (bays - 1);
        const dx = W * t;
        const dw = Math.min(W * 0.13, 13);
        hole(g, k, dx, H * 0.72, dw, H * 0.2);
        // The step. Nothing else in the settlement has one.
        g.rect(dx - dw * 0.72, H * 0.91, dw * 1.44, 3).fill({ color: shade(k.wallLit, 0.9), alpha: 0.95 });
        // The window, lit, beside the door.
        const wx = dx + dw * 1.5;
        if (wx < W * 0.92) {
          hole(g, k, wx, H * 0.66, dw * 0.62, H * 0.11, 0x120f0c);
          g.circle(wx, H * 0.7, dw * 0.5).fill({ color: C.emberLight, alpha: 0.3 });
          hole(g, k, wx, H * 0.665, dw * 0.42, H * 0.085, mix(C.ember, 0xffe9b0, 0.5));
        }
      }
      break;
    }

    /**
     * A lean-to and a stack of cut timber. The log pile, seen end-on, is what
     * makes it a woodcutter's from across the map — nothing else in the
     * settlement is a pyramid of circles.
     */
    case 'camp': {
      const eave = H * 0.42;
      inked(
        g,
        (gg) => gg.poly([W * 0.04, H * 0.94, W * 0.04, eave, W * 0.6, eave - rise * 0.62, W * 0.6, H * 0.86]),
        k.roof,
        3.0,
      );
      g.poly([W * 0.04, H * 0.94, W * 0.04, eave, W * 0.34, eave - rise * 0.31, W * 0.34, H * 0.9]).fill(k.roofLit);
      g.rect(W * 0.03, eave, 3.4, H * 0.94 - eave).fill(k.timber);
      g.rect(W * 0.57, eave - rise * 0.62, 3.4, H * 0.86 - eave + rise * 0.62).fill(k.timber);
      const lr = Math.max(2.6, W * 0.055);
      for (let row = 0; row < 3; row++) {
        for (let i = 0; i <= row; i++) {
          const x = W * 0.78 - row * lr + i * lr * 2;
          const y = H * 0.88 - row * lr * 1.7;
          inked(g, (gg) => gg.circle(x, y, lr), k.timber, 2.0);
          g.circle(x, y, lr * 0.45).fill(shade(k.timber, 1.35));
        }
      }
      break;
    }

    /** A hall with a wheel on the end of it. The wheel is the whole read. */
    case 'sawmill': {
      mass(W * 0.2, W * 0.96, H * 0.32, H * 0.92, rise * 0.75);
      g.rect(W * 0.56, H * 0.62, W * 0.14, H * 0.3).fill(0x14120f);
      const wr = Math.min(H * 0.4, W * 0.19);
      const wx = W * 0.15;
      const wy = H * 0.6;
      inked(g, (gg) => gg.circle(wx, wy, wr), shade(k.timber, 0.8), 3.0);
      g.circle(wx, wy, wr * 0.72).fill({ color: 0x14120f, alpha: 0.85 });
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        g.moveTo(wx, wy)
          .lineTo(wx + Math.cos(a) * wr, wy + Math.sin(a) * wr)
          .stroke({ width: 2.2, color: k.timber });
      }
      inked(g, (gg) => gg.circle(wx, wy, wr * 0.22), k.trim, 1.8);
      if (vis.glow > 0) g.circle(wx, wy, wr * 1.4).fill({ color: k.trim, alpha: 0.2 * vis.glow });
      break;
    }

    /**
     * Sails. Nothing else in the game carries a diagonal cross above it, and it
     * is legible at any zoom at which the building is legible at all.
     */
    case 'windmill': {
      const cx = W / 2;
      const capY = H * 0.5 - rise * 0.9;
      inked(g, (gg) => gg.poly([W * 0.2, H * 0.94, W * 0.32, capY, W * 0.68, capY, W * 0.8, H * 0.94]), k.wall, 3.0);
      g.poly([W * 0.2, H * 0.94, W * 0.32, capY, cx, capY, cx, H * 0.94]).fill(k.wallLit);
      g.rect(W * 0.44, H * 0.68, W * 0.12, H * 0.26).fill(0x14120f);
      inked(g, (gg) => gg.poly([W * 0.28, capY + 2, cx, capY - rise * 0.34, W * 0.72, capY + 2]), k.roof, 2.8);
      const sr = Math.min(W * 0.62, rise * 0.62);
      const sy = capY - rise * 0.04;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.5;
        const ex = cx + Math.cos(a) * sr;
        const ey = sy + Math.sin(a) * sr;
        inkedStroke(g, (gg) => gg.moveTo(cx, sy).lineTo(ex, ey), 3.4, k.timber);
        // A cloth on one side of each arm, so the cross reads as blades.
        const px = -Math.sin(a) * sr * 0.22;
        const py = Math.cos(a) * sr * 0.22;
        g.poly([
          cx + (ex - cx) * 0.35, sy + (ey - sy) * 0.35,
          ex, ey,
          ex + px, ey + py,
          cx + (ex - cx) * 0.35 + px, sy + (ey - sy) * 0.35 + py,
        ]).fill({ color: k.roofLit, alpha: 0.85 });
      }
      inked(g, (gg) => gg.circle(cx, sy, 3.4), k.trim, 2.0);
      break;
    }

    /**
     * Fat, round-shouldered and blind. A granary has no windows and no door
     * anybody can see — grain goes in at the top, off a ladder — which is
     * exactly what tells it apart from a storehouse of the same footprint.
     */
    case 'granary': {
      const cx = W / 2;
      const hw = W * 0.4;
      const topY = H * 0.44 - rise * 0.7;
      // Staddle stones: it stands clear of the ground so the rats cannot climb.
      for (const d of [-0.62, 0, 0.62]) {
        g.rect(cx + d * hw - 3, H * 0.84, 6, H * 0.12).fill(shade(k.wall, 0.7));
        g.ellipse(cx + d * hw, H * 0.84, 6.5, 3).fill(k.wallLit);
      }
      inked(
        g,
        (gg) =>
          gg
            .moveTo(cx - hw, H * 0.86)
            .lineTo(cx - hw * 1.02, topY + hw * 0.5)
            .quadraticCurveTo(cx, topY - hw * 0.12, cx + hw * 1.02, topY + hw * 0.5)
            .lineTo(cx + hw, H * 0.86)
            .closePath(),
        k.wall,
        3.2,
      );
      g.rect(cx - hw, topY + hw * 0.6, hw * 0.66, H * 0.86 - topY - hw * 0.6).fill(k.wallLit);
      // Hoops round the barrel, and one small hatch high up. That is all.
      for (let i = 0; i < 3; i++) {
        const y = topY + hw * 0.8 + i * ((H * 0.84 - topY - hw * 0.75) / 3);
        g.moveTo(cx - hw, y).lineTo(cx + hw, y).stroke({ width: 2.2, color: k.timber, alpha: 0.85 });
      }
      fabricMarks(g, k, cx - hw * 0.9, topY + hw * 0.9, cx + hw * 0.9, H * 0.82, seed);
      hole(g, k, cx, topY + hw * 0.3, hw * 0.34, hw * 0.3);
      // The cap takes the people's pitch and the people's overhang: a granary is
      // a fat blind drum everywhere, and everywhere it is capped differently.
      const gTop = topY - rise * 0.46 * (0.4 + 0.6 * k.pitch);
      const gOver = hw * (1.08 + k.eave * 0.9);
      inked(g, (gg) => gg.poly([cx - gOver, topY + hw * 0.5, cx, gTop, cx + gOver, topY + hw * 0.5]), k.roof, 3.0);
      g.poly([cx - gOver, topY + hw * 0.5, cx, gTop, cx, topY + hw * 0.5]).fill(k.roofLit);
      box = { x0: cx - hw, x1: cx + hw, top: topY + hw * 0.5, bottom: H * 0.86, peak: gTop };
      break;
    }

    /** Long, low, and all doors. A shed is a mouth you back a cart into. */
    case 'store': {
      roofed(W * 0.06, W * 0.94, H * 0.44, H * 0.94, rise * 0.42);
      g.rect(W * 0.3, H * 0.58, W * 0.4, H * 0.36).fill(0x14120f);
      g.rect(W * 0.49, H * 0.58, 3, H * 0.36).fill({ color: k.timber, alpha: 0.9 });
      for (let i = 0; i < 3; i++) {
        g.rect(W * 0.06, H * (0.62 + i * 0.11), W * 0.22, 2.6).fill({ color: k.timber, alpha: 0.6 });
        g.rect(W * 0.72, H * (0.62 + i * 0.11), W * 0.22, 2.6).fill({ color: k.timber, alpha: 0.6 });
      }
      if (vis.detail > 2) {
        // An armoury: a rack of spears standing against the wall.
        for (let i = 0; i < 4; i++) {
          g.moveTo(W * 0.1 + i * 5, H * 0.94)
            .lineTo(W * 0.13 + i * 5, H * 0.28)
            .stroke({ width: 1.8, color: k.trim });
        }
      }
      break;
    }

    /** A chimney the height of the building again, and a mouth full of fire. */
    case 'forge': {
      mass(W * 0.06, W * 0.9, H * 0.34, H * 0.94, rise * 0.4);
      chimney(W * 0.68, W * 0.2, -rise * 0.95, H * 0.4, vis.glow);
      g.rect(W * 0.16, H * 0.56, W * 0.32, H * 0.38).fill(0x14120f);
      g.rect(W * 0.18, H * 0.62, W * 0.28, H * 0.22).fill({ color: C.emberDeep, alpha: 0.85 });
      g.rect(W * 0.21, H * 0.68, W * 0.22, H * 0.1).fill({ color: C.emberLight, alpha: 0.9 });
      if (vis.glow > 0.75) {
        g.ellipse(W * 0.32, H * 0.74, W * 0.3, H * 0.16).fill({ color: C.ember, alpha: 0.25 });
      }
      break;
    }

    /** A dome with a bread-mouth. Round, where a forge is square. */
    case 'oven': {
      const cx = W * 0.42;
      const r = Math.min(W * 0.4, H * 0.42);
      mass(W * 0.04, W * 0.96, H * 0.62, H * 0.94, rise * 0.18);
      inked(g, (gg) => gg.ellipse(cx, H * 0.62, r, r * 0.98), k.wall, 3.2);
      g.ellipse(cx - r * 0.3, H * 0.56, r * 0.48, r * 0.5).fill(k.wallLit);
      // The mouth: an arch, lit from inside.
      g.circle(cx, H * 0.7, r * 0.4).fill(0x14120f);
      g.rect(cx - r * 0.4, H * 0.7, r * 0.8, r * 0.32).fill(0x14120f);
      g.circle(cx, H * 0.71, r * 0.27).fill({ color: C.emberLight, alpha: 0.85 });
      chimney(W * 0.8, W * 0.14, -rise * 0.4, H * 0.66, vis.glow * 0.5);
      break;
    }

    case 'mine': {
      g.ellipse(W * 0.5, H * 0.62, W * 0.36, H * 0.3).fill(0x2a251f);
      g.ellipse(W * 0.5, H * 0.62, W * 0.24, H * 0.19).fill(0x100e0c);
      // Pit-head frame: an A of heavy timber with a winding wheel in the top.
      inkedStroke(g, (gg) => gg.moveTo(W * 0.18, H * 0.94).lineTo(W * 0.5, -rise * 0.55), 3.6, k.timber);
      inkedStroke(g, (gg) => gg.moveTo(W * 0.82, H * 0.94).lineTo(W * 0.5, -rise * 0.55), 3.6, k.timber);
      g.moveTo(W * 0.3, H * 0.35).lineTo(W * 0.7, H * 0.35).stroke({ width: 2.8, color: k.timber });
      inked(g, (gg) => gg.circle(W * 0.5, -rise * 0.4, Math.max(4, W * 0.11)), shade(k.timber, 0.85), 2.4);
      g.circle(W * 0.5, -rise * 0.4, Math.max(1.6, W * 0.04)).fill(k.trim);
      if (vis.detail > 2) {
        // A deep mine has a spoil heap beside the head.
        g.ellipse(W * 0.16, H * 0.86, W * 0.16, H * 0.1).fill(shade(k.wall, 0.65));
      }
      break;
    }

    case 'pit': {
      // A stepped excavation, not a puddle: three terraces down to the dark.
      for (let i = 0; i < 3; i++) {
        const t = i / 3;
        g.ellipse(W * 0.5, H * (0.56 + t * 0.06), W * (0.44 - t * 0.12), H * (0.38 - t * 0.1)).fill(
          shade(k.wall, 0.62 - i * 0.12),
        );
      }
      g.ellipse(W * 0.5, H * 0.66, W * 0.18, H * 0.12).fill(0x0e0c0a);
      if (vis.glow > 0) {
        g.ellipse(W * 0.5, H * 0.64, W * 0.24, H * 0.17).fill({ color: C.emberDeep, alpha: 0.8 });
        g.ellipse(W * 0.5, H * 0.64, W * 0.13, H * 0.09).fill({ color: C.emberLight, alpha: 0.9 });
      }
      break;
    }

    /** Furrows, and a barn on the corner. The barn is what makes it a farm. */
    case 'farm': {
      furrows(g, k, W, H);
      mass(W * 0.02, W * 0.34, H * 0.5, H * 0.9, rise * 0.6);
      g.rect(W * 0.14, H * 0.68, W * 0.1, H * 0.22).fill(0x14120f);
      break;
    }

    /**
     * Not ploughed ground — the opposite of it. A caltrop field is bare earth
     * with things in it that you do not want to walk on, and drawing it with
     * the farm's furrows made the two indistinguishable in the one place the
     * difference matters, which is where the horde is about to walk.
     */
    case 'field': {
      g.rect(2, 2, W - 4, H - 4).fill(0x2a2620);
      g.rect(2, 2, W - 4, H - 4).stroke({ width: 1.6, color: k.timber, alpha: 0.7 });
      for (let i = 0; i < 9; i++) {
        const x = 5 + hash2(i, seed, 211) * (W - 10);
        const y = 5 + hash2(i, seed, 223) * (H - 10);
        const s = 2.6;
        g.moveTo(x - s, y - s).lineTo(x + s, y + s).stroke({ width: 1.9, color: k.trim, alpha: 0.95 });
        g.moveTo(x + s, y - s).lineTo(x - s, y + s).stroke({ width: 1.9, color: k.trim, alpha: 0.95 });
      }
      break;
    }

    /** Bare fenced ground, and whatever is being done on it. */
    case 'yard': {
      g.rect(2, 2, W - 4, H - 4).fill(shade(k.wall, 0.7));
      fence(3, 3, W - 3, H - 3);
      if (vis.detail > 2) {
        // A siege yard: a half-built engine up on the stocks.
        const cx = W * 0.5;
        inkedStroke(g, (gg) => gg.moveTo(cx - W * 0.2, H * 0.8).lineTo(cx + W * 0.05, -rise * 0.5), 3.8, k.timber);
        inkedStroke(g, (gg) => gg.moveTo(cx + W * 0.24, H * 0.8).lineTo(cx + W * 0.05, -rise * 0.5), 3.8, k.timber);
        inkedStroke(g, (gg) => gg.moveTo(cx - W * 0.12, H * 0.42).lineTo(cx + W * 0.18, H * 0.42), 3.0, k.timber);
        inked(g, (gg) => gg.circle(cx - W * 0.16, H * 0.82, Math.max(4, W * 0.08)), shade(k.timber, 0.8), 2.4);
        inked(g, (gg) => gg.circle(cx + W * 0.2, H * 0.82, Math.max(4, W * 0.08)), shade(k.timber, 0.8), 2.4);
      } else if (vis.detail > 0) {
        // A drill yard: churned mud and a pell to hit.
        g.ellipse(W * 0.5, H * 0.6, W * 0.3, H * 0.24).fill({ color: 0x241f19, alpha: 0.6 });
        g.rect(W * 0.48, H * 0.3, 4, H * 0.34).fill(k.timber);
      }
      break;
    }

    /**
     * A hall with a walled yard in front of it, and a flag over the gate.
     * Nothing else in the settlement is a building *and* an enclosure.
     */
    case 'barracks': {
      g.rect(3, H * 0.52, W - 6, H * 0.44).fill(shade(k.wall, 0.66));
      fence(4, H * 0.54, W - 4, H - 4);
      // Shields stacked against the yard wall.
      for (let i = 0; i < 4; i++) {
        g.ellipse(W * (0.16 + i * 0.2), H * 0.86, W * 0.05, H * 0.05).fill({ color: k.trim, alpha: 0.7 });
      }
      mass(W * 0.06, W * 0.94, H * 0.16, H * 0.54, rise * 0.78);
      const doors = Math.max(2, Math.round(W / 34));
      for (let i = 0; i < doors; i++) {
        g.rect(W * (0.14 + (i * 0.72) / doors), H * 0.34, W * 0.1, H * 0.2).fill(0x14120f);
      }
      break;
    }

    /**
     * A shooting lane with butts at the far end. The ringed targets are the
     * read, and they are the only circles-inside-circles in the settlement.
     */
    case 'range': {
      g.rect(2, 2, W - 4, H - 4).fill(shade(k.wall, 0.68));
      fence(3, 3, W - 3, H - 3);
      // The lane, scuffed bare by boots.
      g.rect(W * 0.06, H * 0.36, W * 0.88, H * 0.34).fill({ color: 0x2c2620, alpha: 0.75 });
      for (let i = 0; i < 3; i++) {
        const tx = W * 0.86;
        const ty = H * (0.24 + i * 0.26);
        const r = Math.min(W * 0.075, H * 0.11);
        g.rect(tx - 1.6, ty, 3.2, H * 0.16).fill(k.timber);
        inked(g, (gg) => gg.circle(tx, ty, r), shade(k.timber, 1.1), 2.4);
        g.circle(tx, ty, r * 0.62).fill(k.roof);
        g.circle(tx, ty, r * 0.26).fill(C.oxblood);
      }
      // The shooting line.
      g.rect(W * 0.1, H * 0.14, 3, H * 0.72).fill({ color: k.timber, alpha: 0.85 });
      break;
    }

    /** Awnings on poles, and no walls at all. That is what a market is. */
    case 'market': {
      g.rect(2, 2, W - 4, H - 4).fill(shade(k.wall, 0.72));
      const stalls: Array<[number, number]> = [
        [0.24, 0.36],
        [0.7, 0.32],
        [0.3, 0.76],
        [0.74, 0.74],
      ];
      for (let i = 0; i < stalls.length; i++) {
        const sx = W * stalls[i][0];
        const sy = H * stalls[i][1];
        const hw = Math.min(W * 0.15, 15);
        const lift = rise * (0.5 + (i % 2) * 0.18);
        g.rect(sx - hw, sy, 2.6, hw * 1.1).fill(k.timber);
        g.rect(sx + hw - 2.6, sy, 2.6, hw * 1.1).fill(k.timber);
        // The canopy: a shallow tent, striped.
        inked(g, (gg) => gg.poly([sx - hw * 1.2, sy, sx, sy - lift, sx + hw * 1.2, sy]), k.roof, 2.6);
        g.poly([sx - hw * 1.2, sy, sx, sy - lift, sx, sy]).fill(i % 2 === 0 ? C.oxblood : k.roofLit);
        g.rect(sx - hw, sy + hw * 0.7, hw * 2, 3.4).fill({ color: k.timber, alpha: 0.9 });
      }
      break;
    }

    /**
     * A kerb, a windlass, and light coming up out of the ground. The only
     * building whose silhouette is mostly the thing it stands on.
     */
    case 'well': {
      const cx = W / 2;
      const cy = H * 0.68;
      const r = Math.min(W, H) * 0.3;
      g.ellipse(cx, cy, r * 1.4, r * 0.82).fill(shade(k.wall, 0.55));
      inked(g, (gg) => gg.ellipse(cx, cy, r, r * 0.58), k.wall, 3.0);
      g.ellipse(cx, cy - 1.5, r * 0.7, r * 0.4).fill(0x0b0d10);
      // Two posts and a crossbeam over the mouth.
      for (const d of [-1, 1]) {
        inkedStroke(
          g,
          (gg) => gg.moveTo(cx + d * r * 0.92, cy).lineTo(cx + d * r * 0.74, cy - rise * 0.72),
          3.2,
          k.timber,
        );
      }
      inkedStroke(g, (gg) => gg.moveTo(cx - r, cy - rise * 0.74).lineTo(cx + r, cy - rise * 0.74), 3.0, k.timber);
      // The column of light — widest at the top, so it reads as rising.
      g.poly([cx - r * 0.5, cy, cx + r * 0.5, cy, cx + r * 0.9, -rise * 0.9, cx - r * 0.9, -rise * 0.9]).fill({
        color: 0x9fd8ff,
        alpha: 0.1 + vis.glow * 0.12,
      });
      g.ellipse(cx, cy - 2, r * 0.6, r * 0.34).fill({ color: 0xdff0ff, alpha: 0.55 });
      break;
    }

    /** A shrine is narrow and it reaches. That is the whole of it. */
    case 'shrine': {
      mass(W * 0.26, W * 0.74, H * 0.44, H * 0.94, rise * 1.0);
      hole(g, k, W * 0.5, H * 0.68, W * 0.14, H * 0.26);
      // The finial hangs off the crown the people actually built, not off a
      // fixed height — a slab-capped dwarven shrine and a spired Vigil one put
      // their tops in very different places, and a mark floating in the sky
      // above a gap belongs to neither.
      const fy = Math.min(box.peak, box.top) - rise * 0.12;
      g.rect(W * 0.47, fy - rise * 0.44, W * 0.06, rise * 0.46).fill(k.trim);
      g.rect(W * 0.38, fy - rise * 0.28, W * 0.24, Math.max(2.4, W * 0.05)).fill(k.trim);
      if (vis.glow > 0) {
        g.circle(W * 0.5, H * 0.66, W * 0.16).fill({ color: k.trim, alpha: 0.35 });
      }
      break;
    }

    /** One arm out from one post. It should look like what it is. */
    case 'gibbet': {
      const px = W * 0.32;
      inkedStroke(g, (gg) => gg.moveTo(px, H * 0.94).lineTo(px, -rise * 0.9), 4.0, k.timber);
      inkedStroke(g, (gg) => gg.moveTo(px, -rise * 0.9).lineTo(W * 0.86, -rise * 0.9), 3.4, k.timber);
      inkedStroke(g, (gg) => gg.moveTo(px + 4, -rise * 0.6).lineTo(W * 0.72, -rise * 0.86), 2.2, k.timber);
      g.moveTo(W * 0.78, -rise * 0.88).lineTo(W * 0.78, -rise * 0.44).stroke({ width: 2.0, color: k.trim });
      g.circle(W * 0.78, -rise * 0.34, Math.max(3, W * 0.1)).stroke({ width: 2.2, color: k.trim });
      g.ellipse(W * 0.5, H * 0.86, W * 0.34, H * 0.12).fill({ color: 0x1a1613, alpha: 0.6 });
      break;
    }

    /** A bowl of fire on three legs. Small, and the brightest thing near it. */
    case 'brazier': {
      const cx = W / 2;
      const bowl = H * 0.32;
      for (const d of [-1, 0, 1]) {
        inkedStroke(g, (gg) => gg.moveTo(cx + d * W * 0.24, H * 0.94).lineTo(cx + d * W * 0.06, bowl), 2.8, k.trim);
      }
      inked(
        g,
        (gg) =>
          gg.poly([
            cx - W * 0.32, bowl - 4,
            cx + W * 0.32, bowl - 4,
            cx + W * 0.2, bowl + H * 0.16,
            cx - W * 0.2, bowl + H * 0.16,
          ]),
        shade(k.wall, 0.8),
        2.6,
      );
      for (let i = 0; i < 3; i++) {
        g.circle(cx, bowl - rise * (0.12 + i * 0.14), W * (0.3 - i * 0.08)).fill({
          color: [C.emberDeep, C.ember, C.emberLight][i],
          alpha: 0.55 + i * 0.15,
        });
      }
      break;
    }

    default:
      mass(W * 0.1, W * 0.9, H * 0.28, H * 0.94, rise * 0.6);
      break;
  }

  /**
   * A motif is sized against the crown it sits on, not against the texture's
   * headroom. A storehouse's roof is a fifth the height of a hall's, and a crane
   * scaled to the hall left the storehouse with a jib in the sky attached to
   * nothing. The floor stops a caltrop field's motif vanishing entirely.
   */
  drawMotif(g, k, vis.motif, box, Math.max(rise * 0.3, (box.top - box.peak) * 0.95), vis.glow);

  if (vis.banner) {
    g.moveTo(W * 0.12, H * 0.9)
      .lineTo(W * 0.12, -rise * 0.5)
      .stroke({ width: 3.4, color: INK, alpha: 0.85 });
    g.moveTo(W * 0.12, H * 0.9)
      .lineTo(W * 0.12, -rise * 0.5)
      .stroke({ width: 2.2, color: 0x332c24 });
    const flag = [
      W * 0.12, -rise * 0.5,
      W * 0.12 + 15, -rise * 0.42,
      W * 0.12 + 12, -rise * 0.2,
      W * 0.12 + 15, -rise * 0.02,
      W * 0.12, -rise * 0.08,
    ];
    g.poly(flag).fill({ color: INK, alpha: 0.85 }).stroke({ width: 3, color: INK, alpha: 0.85, join: 'round' });
    g.poly(flag).fill(C.oxblood);
  }
}

/**
 * The identifying feature, drawn on the roofline of whatever mass the shape
 * left behind.
 *
 * This is the part of the building pass that mattered most, and the reason is
 * arithmetic: twenty-five shapes against eighty-eight buildings meant thirteen
 * of them were a plain box with a crown on it, so a tavern, an academy, a war
 * room and a counting house were the same drawing four times. The fix is not
 * thirteen more shapes — that is thirteen more things a player has to learn —
 * it is *one feature each, above the eaves*, chosen from a list short enough to
 * be learned once and long enough to go round.
 *
 * Two rules, both paid for elsewhere in this file. **Above the eaves**: a mark
 * on a wall is invisible at four tiles and a box on a ridge is not. And **in the
 * people's own materials**: the motif says what the building is *for*, the
 * vernacular says who built it, and neither is allowed to answer the other's
 * question.
 */
function drawMotif(
  g: Graphics,
  k: ArchSkin,
  motif: BuildingMotif,
  box: { x0: number; x1: number; top: number; bottom: number; peak: number },
  rise: number,
  glow: number,
): void {
  if (motif === 'none') return;
  const cx = (box.x0 + box.x1) / 2;
  const hw = Math.max(5, (box.x1 - box.x0) / 2);
  /**
   * The datum is the *ridge*, not the wall top.
   *
   * The first render of this put every motif at the wall head, which for a
   * roofed building is inside its own roof: the chimneys on the hovels came out
   * shorter than the ridge they were supposed to be poking through and were
   * invisible in the picture. A motif that does not break the outline is not a
   * motif, it is a decal.
   */
  const top = Math.min(box.top, box.peak);
  const s = Math.max(4, Math.min(hw * 0.52, rise * 0.44, 20));

  /** A stack, soot-mouthed, standing clear of the ridge. */
  const stack = (x: number, wide: number, height: number) => {
    const foot = box.top + 4;
    inked(g, (gg) => gg.rect(x - wide / 2, top - height, wide, foot - top + height), shade(k.wall, 0.84), 2.6);
    g.rect(x - wide / 2, top - height, wide * 0.38, foot - top + height).fill(shade(k.wallLit, 0.92));
    g.rect(x - wide / 2 - 1.4, top - height, wide + 2.8, 3).fill(shade(k.wall, 0.55));
    // The soot, and one wisp. A cold chimney is a house nobody is in.
    g.ellipse(x, top - height - 3, wide * 0.5, 2).fill({ color: 0x1a1714, alpha: 0.75 });
    g.ellipse(x - wide * 0.24, top - height - 8, wide * 0.5, 3.4).fill({ color: 0x3a352e, alpha: 0.5 });
  };

  switch (motif) {
    case 'chimney':
      // At the gable end, clear of the ridge, because a stack buried in its own
      // roof reads as nothing at all.
      stack(box.x1 - hw * 0.22, Math.max(5, s * 0.62), rise * 0.4);
      break;
    case 'chimneys':
      for (let i = -1; i <= 1; i++) {
        stack(cx + i * hw * 0.68, Math.max(4.2, s * 0.5), rise * (0.34 - Math.abs(i) * 0.06));
      }
      break;

    /** A board on a bracket. You go in here and somebody serves you. */
    case 'sign': {
      const px = box.x0 - 2;
      inkedStroke(g, (gg) => gg.moveTo(px, box.bottom).lineTo(px, top - rise * 0.5), 3.2, k.timber);
      inkedStroke(g, (gg) => gg.moveTo(px, top - rise * 0.5).lineTo(px - s * 1.5, top - rise * 0.5), 2.4, k.timber);
      inked(g, (gg) => gg.rect(px - s * 1.7, top - rise * 0.44, s * 1.5, s * 1.1), shade(k.timber, 1.2), 2.4);
      g.rect(px - s * 1.5, top - rise * 0.38, s * 1.1, 2).fill({ color: k.trim, alpha: 0.8 });
      g.rect(px - s * 1.5, top - rise * 0.28, s * 0.8, 2).fill({ color: k.trim, alpha: 0.6 });
      break;
    }

    /** A clerestory: a long lit box riding the ridge. Somewhere they read. */
    case 'lantern': {
      const w = hw * 1.1;
      inked(g, (gg) => gg.rect(cx - w / 2, top - rise * 0.62, w, rise * 0.4), shade(k.wall, 0.9), 2.6);
      const n = Math.max(3, Math.round(w / 9));
      for (let i = 0; i < n; i++) {
        const x = cx - w / 2 + (w * (i + 0.5)) / n;
        g.rect(x - 1.4, top - rise * 0.56, 2.8, rise * 0.26).fill({
          color: mix(C.ember, 0xffe9b0, 0.4),
          alpha: 0.75 + glow * 0.2,
        });
      }
      inked(g, (gg) => gg.rect(cx - w / 2 - 3, top - rise * 0.7, w + 6, rise * 0.1), k.roof, 2.4);
      break;
    }

    /** A drum, a dome and a finial. Somewhere a decision is signed. */
    case 'cupola': {
      const r = s * 1.15;
      inked(g, (gg) => gg.rect(cx - r * 0.8, top - rise * 0.5, r * 1.6, rise * 0.32), shade(k.wall, 0.92), 2.4);
      inked(g, (gg) => gg.ellipse(cx, top - rise * 0.5, r, r * 0.9), k.roofLit, 2.6);
      inkedStroke(g, (gg) => gg.moveTo(cx, top - rise * 0.5 - r * 0.85).lineTo(cx, top - rise * 0.5 - r * 1.9), 2.2, k.trim);
      g.circle(cx, top - rise * 0.5 - r * 2, 2).fill(k.trim);
      break;
    }

    /** A jib and a hook. Goods go up here, and the arm is the read. */
    case 'crane': {
      const bx = box.x1 - hw * 0.2;
      inkedStroke(g, (gg) => gg.moveTo(bx, top + 4).lineTo(bx, top - rise * 0.85), 3.4, k.timber);
      inkedStroke(g, (gg) => gg.moveTo(bx, top - rise * 0.85).lineTo(bx + hw * 0.75, top - rise * 0.6), 2.8, k.timber);
      inkedStroke(g, (gg) => gg.moveTo(bx, top - rise * 0.5).lineTo(bx + hw * 0.62, top - rise * 0.66), 2, k.timber);
      const hx = bx + hw * 0.72;
      g.moveTo(hx, top - rise * 0.61).lineTo(hx, top - rise * 0.24).stroke({ width: 1.6, color: 0x1a1714 });
      inked(g, (gg) => gg.rect(hx - s * 0.42, top - rise * 0.24, s * 0.84, s * 0.62), shade(k.timber, 0.8), 2);
      break;
    }

    /** A stack of barrels, seen end-on. Nothing else is a pyramid of rings. */
    case 'barrels': {
      const br = Math.max(2.8, s * 0.44);
      const ox = box.x1 - br * 1.4;
      for (let row = 0; row < 2; row++) {
        for (let i = 0; i <= row; i++) {
          const x = ox - row * br + i * br * 2;
          const y = box.bottom - br - row * br * 1.75;
          inked(g, (gg) => gg.circle(x, y, br), shade(k.timber, 0.85), 2);
          g.circle(x, y, br * 0.5).fill(shade(k.timber, 1.3));
        }
      }
      break;
    }

    /** A skull, and a spread of antlers. Things are killed here. */
    case 'antlers': {
      inked(g, (gg) => gg.ellipse(cx, top - rise * 0.24, s * 0.6, s * 0.72), C.bone, 2.2);
      g.circle(cx - s * 0.22, top - rise * 0.24, s * 0.14).fill(0x14120f);
      g.circle(cx + s * 0.22, top - rise * 0.24, s * 0.14).fill(0x14120f);
      for (const d of [-1, 1]) {
        inkedStroke(
          g,
          (gg) =>
            gg
              .moveTo(cx + d * s * 0.5, top - rise * 0.34)
              .quadraticCurveTo(cx + d * s * 1.7, top - rise * 0.5, cx + d * s * 1.5, top - rise * 0.95),
          2.2,
          C.bone,
        );
        inkedStroke(
          g,
          (gg) => gg.moveTo(cx + d * s * 1.15, top - rise * 0.52).lineTo(cx + d * s * 2.1, top - rise * 0.66),
          1.8,
          C.bone,
        );
      }
      break;
    }

    /** A covered vessel with a worm coming off it. */
    case 'still': {
      const r = s * 0.9;
      const vx = cx - hw * 0.3;
      const vy = top - rise * 0.34;
      inked(g, (gg) => gg.ellipse(vx, vy, r, r * 0.86), shade(k.trim, 0.85), 2.4);
      inked(
        g,
        (gg) => gg.poly([vx - r * 0.5, vy - r * 0.6, vx, vy - r * 1.5, vx + r * 0.5, vy - r * 0.6]),
        shade(k.trim, 0.7),
        2,
      );
      for (let i = 0; i < 3; i++) {
        g.circle(cx + hw * 0.2 + i * r * 0.2, top - rise * (0.5 - i * 0.1), r * 0.44).stroke({
          width: 2,
          color: shade(k.trim, 1.05),
        });
      }
      g.circle(vx, vy + r * 0.4, r * 0.42).fill({ color: C.ember, alpha: 0.35 + glow * 0.3 });
      break;
    }

    /** A great toothed wheel, turning nothing you can see. */
    case 'cog': {
      const r = Math.max(5, Math.min(hw * 0.7, rise * 0.6));
      const wx = box.x0 + r * 0.5;
      const wy = top - r * 0.15;
      inked(g, (gg) => gg.circle(wx, wy, r), shade(k.wall, 0.8), 2.8);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        g.rect(wx + Math.cos(a) * r * 1.02 - 2, wy + Math.sin(a) * r * 1.02 - 2, 4, 4).fill(shade(k.wallLit, 1.1));
      }
      g.circle(wx, wy, r * 0.4).fill(0x14120f);
      g.circle(wx, wy, r * 0.16).fill(k.trim);
      break;
    }

    /** A beam and two pans. Somewhere they count, and the count is binding. */
    case 'scales': {
      const by = top - rise * 0.66;
      inkedStroke(g, (gg) => gg.moveTo(cx, top + 2).lineTo(cx, by), 2.8, k.trim);
      inkedStroke(g, (gg) => gg.moveTo(cx - s * 1.3, by - s * 0.24).lineTo(cx + s * 1.3, by + s * 0.24), 2.4, k.trim);
      for (const d of [-1, 1]) {
        const px = cx + d * s * 1.3;
        const py = by + d * s * 0.24;
        g.moveTo(px, py).lineTo(px, py + s * 0.5).stroke({ width: 1.4, color: k.trim });
        inked(g, (gg) => gg.ellipse(px, py + s * 0.6, s * 0.5, s * 0.22), shade(k.trim, 0.85), 2);
      }
      break;
    }

    /** Cages, hung off a beam. Sometimes occupied. */
    case 'cages': {
      const by = top - rise * 0.6;
      inkedStroke(g, (gg) => gg.moveTo(box.x0 + hw * 0.2, by).lineTo(box.x1 - hw * 0.2, by - rise * 0.06), 2.8, k.timber);
      for (const t of [0.3, 0.72]) {
        const px = box.x0 + (box.x1 - box.x0) * t;
        g.moveTo(px, by).lineTo(px, by + s * 0.5).stroke({ width: 1.4, color: 0x1a1714 });
        inked(g, (gg) => gg.rect(px - s * 0.42, by + s * 0.5, s * 0.84, s * 1.1), 0x1a1714, 2);
        for (let i = 0; i < 3; i++) {
          g.moveTo(px - s * 0.42 + (i * s * 0.84) / 2, by + s * 0.5)
            .lineTo(px - s * 0.42 + (i * s * 0.84) / 2, by + s * 1.6)
            .stroke({ width: 1.2, color: k.trim, alpha: 0.8 });
        }
      }
      break;
    }

    /** A rack of shafts against the wall. Spears, tools, or stakes. */
    case 'rack': {
      const n = 5;
      for (let i = 0; i < n; i++) {
        const x = box.x0 + hw * 0.12 + i * (hw * 0.24);
        g.moveTo(x, box.bottom).lineTo(x + s * 0.34, top - rise * 0.5).stroke({ width: 1.8, color: k.timber });
        g.circle(x + s * 0.34, top - rise * 0.5, 1.5).fill({ color: k.trim, alpha: 0.9 });
      }
      g.moveTo(box.x0 + hw * 0.1, top + 4)
        .lineTo(box.x0 + hw * 0.12 + n * hw * 0.24, top + 4)
        .stroke({ width: 2, color: k.timber });
      break;
    }

    /** A row of columns and a lintel across them. The state, standing up straight. */
    case 'colonnade': {
      // A portico across the middle of the front, not a picket fence over the
      // whole of it — the first cut filled the entire facade and the building
      // behind it stopped existing.
      const n = Math.min(6, Math.max(3, Math.round(hw / 11)));
      const px0 = cx - hw * 0.62;
      const px1 = cx + hw * 0.62;
      const y0 = box.bottom - (box.bottom - box.top) * 0.62;
      const y1 = box.bottom - 2;
      inked(g, (gg) => gg.rect(px0 - 3, y0 - 6, px1 - px0 + 6, 7), k.wallLit, 2.4);
      for (let i = 0; i < n; i++) {
        const x = px0 + ((px1 - px0) * (i + 0.5)) / n;
        inked(g, (gg) => gg.rect(x - 2.2, y0, 4.4, y1 - y0), k.wallLit, 2.2);
      }
      break;
    }

    /** Rings and a sphere, and somebody is certain about the sky. */
    case 'orrery': {
      const r = Math.max(4, Math.min(hw * 0.5, rise * 0.4));
      const oy = top - rise * 0.62;
      inkedStroke(g, (gg) => gg.moveTo(cx, top + 2).lineTo(cx, oy + r * 0.6), 2.4, k.trim);
      g.ellipse(cx, oy, r, r * 0.34).stroke({ width: 2, color: k.trim });
      g.ellipse(cx, oy, r * 0.62, r * 0.9).stroke({ width: 1.8, color: shade(k.trim, 0.85) });
      g.circle(cx, oy, r * 0.28).fill({ color: mix(k.trim, 0xffe9b0, 0.4), alpha: 0.9 });
      g.circle(cx, oy, r * 0.62).fill({ color: k.trim, alpha: 0.12 + glow * 0.14 });
      break;
    }

    default:
      break;
  }
}
